import { Turbo } from '@hotwired/turbo-rails'
import { Controller } from 'stimulus'
import { format } from 'src/utils/units_format'

const FLEET_GAP_IN = 144
const BUCKET_GAP_IN = 180
const TRUCK_GROUP_GAP_IN = 72
const ROW_WIDTH_MULTIPLIER = 1.15
const BULK_QUOTE_CHARGE_CONFIRM_THRESHOLD = 20
const MISSING_DIMENSION_PLACEHOLDER_IN = 72
const MOVE_TRANSFER_ANIMATION_TIMEOUT_MS = 2500

// Spacing for grouped preview views: a wide gap between bands, and how far in
// front of each band its floating label sits.
const CLUSTER_GAP_IN = 240
const CLUSTER_LABEL_GAP_IN = 96
// Reason buckets, ordered the way they stack front-to-back. Heavy/Wide/Tall/Long
// mirror the dimension thresholds the server stamps onto each piece; pieces
// with no oversized dimension fall through to the unlabeled tail.
const LOAD_REASON_ORDER = ['heavy', 'wide', 'tall', 'long']
const LOAD_REASON_LABELS = { heavy: 'Heavy', wide: 'Wide', tall: 'Tall', long: 'Long' }
const LOAD_CLASSIFICATION_ORDER = ['superload', 'overweight', 'od', 'normal']
const LOAD_CLASSIFICATION_LABELS = {
  superload: 'Superload',
  overweight: 'Overweight',
  od: 'OD',
  normal: 'Normal',
}

export default class extends Controller {
  static targets = [
    'payload',
    'fleetContainer',
    'table',
    'rowGroup',
    'row',
    'rowCheckbox',
    'selectAllCheckbox',
    'detail',
    'filterBar',
    'filterLabel',
    'previewBack',
    'previewBackLabel',
    'previewState',
    'previewStatus',
    'bulkPanel',
    'bulkSelectedCount',
    'bulkQuoteAction',
    'bulkMergeButton',
    'bulkSplitButton',
    'bulkSplitAction',
    'bulkDeleteAction',
    'quoteConfirmModal',
    'quoteConfirmMessage',
    'quoteConfirmAccept',
    'modal',
    'modalTitle',
    'modalForm',
    'modalError',
    'modalIntro',
    'modalSelectGroup',
    'modalSelect',
    'modalSubmit',
    'mutationForm',
    'movePanel',
    'movePanelTitle',
    'movePanelStatus',
    'newTruckOptions',
    'actionBarBadge',
    'actionBarVitals',
    'actionBarTargets',
    'addTruckButton',
    'addTruckPopover',
    'addTruckForm',
    'addTruckRoute',
    'addTruckEquipment',
    'addTruckEquipmentSearch',
  ]

  static values = {
    assignUnplannedLoadUrl: String,
    moveLoadUrl: String,
    moveTargetsUrl: String,
    mergeTargetsUrl: String,
    mergeTrucksUrl: String,
    removeLoadUrl: String,
  }

  connect() {
    this.connected = true
    this.selectedKey = this.initialSelectedKey()
    this.selectedGroupKeys = new Set()
    this.approvedQuoteSelectionSignature = null
    this.pendingQuoteConfirmForm = null
    this.pendingQuoteConfirmSignature = null
    this.currentPreviewLoadRfqUnitId = this.readInitialPreviewLoadRfqUnitId()
    this.activeFilter = null
    this.selectionParentFilter = null
    this.moveReturnFilter = null
    this.activeMovePayload = null
    this.moveSceneSignature = null
    this.moveTargetsByKey = new Map()
    this.moveTargetsCacheByRfqUnitId = new Map()
    this.moveTargetsAbortController = null
    this.defaultMovePreviewTarget = null
    this.selectedMoveTarget = null
    this.pendingMoveCompletion = null
    this.preserveMoveWorkspace = false
    this.activeMergePayload = null
    this.mergeTargetsByKey = new Map()
    this.mergeTargetsAbortController = null
    this.splitPanelOpen = false
    this.splitArmedGroupKey = null
    this.comparisonSceneActive = false
    this.handleFleetItemSelected = this.handleFleetItemSelected.bind(this)
    this.handleFleetLoadSelected = this.handleFleetLoadSelected.bind(this)
    this.handleFleetItemHovered = this.handleFleetItemHovered.bind(this)
    this.handleFleetEmptyClicked = this.handleFleetEmptyClicked.bind(this)
    this.handleMutationFormSubmit = this.handleMutationFormSubmit.bind(this)
    this.handleMoveKeydown = this.handleMoveKeydown.bind(this)
    this.handleAddTruckOutsideClick = this.handleAddTruckOutsideClick.bind(this)
    this.handleUnplannedEquipmentSearchFocus = this.handleUnplannedEquipmentSearchFocus.bind(this)
    this.handleSummaryClick = this.handleSummaryClick.bind(this)
    this.handleSummaryKeydown = this.handleSummaryKeydown.bind(this)

    this.fleetContainerTarget.addEventListener('fleet:item-selected', this.handleFleetItemSelected)
    this.fleetContainerTarget.addEventListener('fleet:load-selected', this.handleFleetLoadSelected)
    this.fleetContainerTarget.addEventListener('fleet:item-hovered', this.handleFleetItemHovered)
    this.fleetContainerTarget.addEventListener('fleet:empty-clicked', this.handleFleetEmptyClicked)
    this.element.addEventListener('submit', this.handleMutationFormSubmit, true)
    document.addEventListener('keydown', this.handleMoveKeydown)
    document.addEventListener('click', this.handleAddTruckOutsideClick)
    document.addEventListener('focusin', this.handleUnplannedEquipmentSearchFocus)
    // The summary panel lives outside this controller's element (next to the
    // map), so its bucket rows are wired through delegated document listeners.
    document.addEventListener('click', this.handleSummaryClick)
    document.addEventListener('keydown', this.handleSummaryKeydown)

    this.mount()

    // Mutation turbo streams replace the whole #project-load-management-workspace,
    // which re-renders the sibling RFQ bucket tables. Their best_in_place cells
    // are otherwise only bound on the initial page load / AJAX refresh, so re-bind
    // them the same way the load-management refresh partial does — this keeps
    // inline editing working after a move/merge/split/delete.
    this.reinitializeRfqsBucketWidgets()

    // A workspace mutation can change the project's lanes (e.g. a move/merge
    // reroutes a truck). The map column lives outside this controller and is not
    // touched by the turbo-stream, so nudge its poller to refetch. Guarded so an
    // absent manager (a project that never showed the map) is a harmless no-op.
    if (window.ProjectRoutesMapManager) {
      window.ProjectRoutesMapManager.poke()
    }
  }

  reinitializeRfqsBucketWidgets() {
    if (typeof jQuery === 'undefined') {
      return
    }

    const workspace = this.element.closest('#project-load-management-workspace') || document
    const container = workspace.querySelector('[data-behavior~="project-rfqs-index-container"]')
    if (!container) {
      return
    }

    const $container = jQuery(container)
    const app = window.App
    if (app && app.rfq && typeof app.rfq.reinitializeProjectRfqsUi === 'function') {
      app.rfq.reinitializeProjectRfqsUi($container)
    } else {
      $container.find('.best_in_place').best_in_place()
      if (jQuery.fn.timeago) {
        jQuery('time.timeago').timeago()
      }
    }
  }

  disconnect() {
    this.connected = false
    this.fleetContainerTarget.removeEventListener('fleet:item-selected', this.handleFleetItemSelected)
    this.fleetContainerTarget.removeEventListener('fleet:load-selected', this.handleFleetLoadSelected)
    this.fleetContainerTarget.removeEventListener('fleet:item-hovered', this.handleFleetItemHovered)
    this.fleetContainerTarget.removeEventListener('fleet:empty-clicked', this.handleFleetEmptyClicked)
    this.element.removeEventListener('submit', this.handleMutationFormSubmit, true)
    this.clearFleetEmptyHint()
    document.removeEventListener('keydown', this.handleMoveKeydown)
    document.removeEventListener('click', this.handleAddTruckOutsideClick)
    document.removeEventListener('focusin', this.handleUnplannedEquipmentSearchFocus)
    document.removeEventListener('click', this.handleSummaryClick)
    document.removeEventListener('keydown', this.handleSummaryKeydown)
    this.abortMoveTargetsRequest()
    this.abortMergeTargetsRequest()
    this.selectedGroupKeys.clear()
    this.clearHoveredRow()
    this.destroyRenderers()
    this.destroyAddTruckEquipmentSearch()
    this.hideQuoteConfirmModal()
    this.hideModal()
  }

  async mount() {
    const pendingFleetView = this.consumePendingFleetViewRequest()
    const payload = this.renderPayload
    const fleetItems = payload && Array.isArray(payload.fleet_items) ? payload.fleet_items : []
    const unplannedItems = payload && Array.isArray(payload.unplanned_load_items) ? payload.unplanned_load_items : []
    this.itemsByKey = new Map([...fleetItems, ...unplannedItems].map((item) => [item.key, item]))
    // Server-rendered truth for the "Unassigned" loads-table filter:
    // all_unplanned_rfq_ids is stamped from the raw unplanned units BEFORE the
    // presenter builds per-item payloads, so neither presenter-side item drops
    // nor the 3D preparation below can make the filter hide a genuinely
    // unassigned row. The item-derived set remains as a fallback for stale
    // payloads.
    const serverUnplannedRfqIds = payload && Array.isArray(payload.all_unplanned_rfq_ids)
      ? payload.all_unplanned_rfq_ids
      : unplannedItems.map((item) => item && item.rfq_id)
    this.allUnplannedRfqIds = new Set(
      serverUnplannedRfqIds.map((value) => (value != null ? String(value) : null)).filter(Boolean)
    )
    this.newTrailerOptions = payload && Array.isArray(payload.new_trailer_options) ? payload.new_trailer_options : []

    if (!fleetItems.length && !unplannedItems.length) {
      this.showFleetPlaceholder('fleet_items_missing')
      this.syncBulkSelection()
      return
    }

    try {
      const trailer3d = await import('src/tractor_trailer_3d')
      if (!this.connected) return

      const preparedItems = this.prepareFleetItems(fleetItems, trailer3d)
      const preparedUnplannedItems = this.prepareUnplannedItems(unplannedItems, preparedItems)
      if (!preparedItems.length && !preparedUnplannedItems.length) {
        this.showFleetPlaceholder('fleet_items_unrenderable')
        return
      }

      this.fleetItemsByKey = new Map(preparedItems.map((item) => [item.key, item]))
      this.unplannedItemsByKey = new Map(preparedUnplannedItems.map((item) => [item.key, item]))
      this.itemsByKey = new Map([...preparedItems, ...preparedUnplannedItems].map((item) => [item.key, item]))
      this.sortedKeys = preparedItems.map((item) => item.key)
      // Kept so the preview can be re-laid-out into filtered/grouped views
      // (and back to the base grid) without rebuilding the prepared items.
      this.fleetPreparedItems = preparedItems
      this.baseUnplannedItems = preparedUnplannedItems
      this.currentPreviewRenderSignature = 'base'
      this.fleetRenderer = trailer3d.createFleetRenderer(this.fleetContainerTarget)

      const initialKey = this.initialSelectedKey()
      const previewCleared = this.previewClearedByServer()
      const initialFleetKey = initialKey && this.fleetItemsByKey.has(initialKey) ? initialKey : null
      const selectedRenderKey = previewCleared || pendingFleetView ? null : initialFleetKey
      if (selectedRenderKey) {
        this.ensureRowExpanded(selectedRenderKey)
      }
      this.applyExpansionToDom()
      this.syncBulkSelection()

      this.fleetRenderer.render({
        items: preparedItems,
        unplannedItems: preparedUnplannedItems,
        selectedKey: selectedRenderKey,
        autoSelect: false,
        showScaleFigure: true,
      })

      if (selectedRenderKey) {
        this.applySelectedKey(selectedRenderKey, { preserveLoadSelection: true })
      } else {
        this.clearSelectedKey()
      }
      this.applyFilter()
      if (selectedRenderKey) this.renderTruckSelectedHint(selectedRenderKey)
      this.restorePendingFilter()
      this.restorePendingRowSelection()
      this.restorePendingMoveSelection()
    } catch (error) {
      this.showFleetPlaceholder(error instanceof Error ? error.message : 'fleet_render_mount_failed')
    }
  }

  handleFleetItemSelected(event) {
    const key = event.detail && event.detail.key
    if (!key) return
    if (this.moveCompletionPending()) {
      this.continueMoveFromCompletedTarget(key)
      return
    }

    if (this.openMoveConfirmationForExistingTarget(key)) {
      return
    }

    if (this.pieceSelectionActive()) {
      // The source truck is already identified in the staged scene. Clicking
      // its tractor or deck should not replace the useful destination status
      // with a redundant "current truck" explanation.
      if (String(key) === String(this.activeMoveSourceKey)) return
      this.renderMovePanelNonTarget(key)
      return
    }

    if (this.openMergeConfirmationForTarget(key)) {
      return
    }

    // Priced trucks live in the Priced Loads table, not the Equipment table —
    // a canvas click on one jumps to its row there instead of filtering.
    const item = this.itemsByKey && this.itemsByKey.get(key)
    if (item && item.source_type === 'quote_plan') {
      this.scrollToPricedQuoteRow(key)
      return
    }

    const reselected = this.selectedKey === key
    if (!reselected && this.activeFilter && this.activeFilter.type !== 'keys') {
      this.selectionParentFilter = this.activeFilter
    }
    this.clearMoveMode({ preservePreviewLoad: reselected })
    this.clearSplitMode()
    this.applySelectedKey(key, { preserveLoadSelection: reselected })
    // The keys filter hides every other truck group, and selections follow
    // visibility — so an inspection click must not wipe a pending bulk
    // selection. Filtering on canvas click only happens with nothing checked.
    if (this.selectedGroupKeys.size === 0) {
      this.setFilter(this.filterForItemKey(key))
    }
    // Idle truck selection: nudge the user toward the piece-move workflow. Any
    // real mode (move / merge / split) took an early return above, and the
    // clearMoveMode/clearSplitMode calls guarantee we are idle here. The hint is
    // replaced the moment a piece is picked (selectLoadForMove → renderMovePanel)
    // or a mode starts, so it never lingers mid-workflow.
    this.renderTruckSelectedHint(key)
  }

  handleFleetLoadSelected(event) {
    const key = event.detail && event.detail.key
    const rfqUnitId = event.detail && event.detail.rfqUnitId
    if (!(key && rfqUnitId)) return
    if (this.moveCompletionPending()) {
      this.continueMoveFromCompletedSource(key, rfqUnitId)
      return
    }

    if (this.pieceSelectionActive() && String(key) !== String(this.activeMoveSourceKey)) {
      // Clicking a piece that can carry the moving piece means "stack on it",
      // not "move onto its truck's deck".
      const stackTarget = this.stackTargetFor(key, rfqUnitId)
      if (stackTarget) {
        this.selectMoveTarget(stackTarget)
        return
      }
    }

    if (this.openMoveConfirmationForExistingTarget(key)) {
      return
    }

    const item = this.itemsByKey && this.itemsByKey.get(key)
    const reselectedUnplanned = Boolean(
      item &&
      item.source_type === 'unplanned_load' &&
      this.pieceSelectionActive() &&
      String(this.activeMoveSourceKey || '') === String(key) &&
      String(this.currentPreviewLoadRfqUnitId || '') === String(rfqUnitId)
    )
    if (reselectedUnplanned) {
      this.previewBack()
      return
    }

    if (this.pieceSelectionActive()) {
      // Loads on the source truck remain real choices while move mode is open.
      // Lower the old selection and lift the newly clicked piece without
      // forcing the operator back through truck selection.
      if (String(key) === String(this.activeMoveSourceKey)) {
        if (String(rfqUnitId) !== String(this.currentPreviewLoadRfqUnitId)) {
          this.selectLoadForMove(key, rfqUnitId)
        }
        return
      }
      this.renderMovePanelNonTarget(key)
      return
    }

    // In merge mode a click on a piece counts as a click on its truck.
    if (this.openMergeConfirmationForTarget(key)) {
      return
    }

    // Quote-backed loads are already priced and are not part of the mutable
    // draft assignment scope. Treat a click on the load exactly like a click
    // on its priced truck: take the operator to the quote row instead of
    // lifting it into a move flow that can only report a stale-load error.
    if (item && item.source_type === 'quote_plan') {
      this.scrollToPricedQuoteRow(key)
      return
    }

    if (item && item.source_type === 'unplanned_load') {
      // Toggle (not set) so re-clicking the same loose-load box clears the
      // filter — matching the summary rows' behavior. Skipped while a bulk
      // selection is pending: the keys filter hides every truck group and
      // selections follow visibility, so it would wipe the pending selection.
      if (this.selectedGroupKeys.size === 0) {
        if (this.activeFilter && this.activeFilter.type !== 'keys') {
          this.selectionParentFilter = this.activeFilter
        }
        this.toggleFilter(this.filterForItemKey(key))
      }
    }
    this.selectLoadForMove(key, rfqUnitId)
  }

  handleFleetItemHovered(event) {
    const key = event.detail && event.detail.key
    if (this.moveCompletionPending()) return
    if (this.pieceSelectionActive()) {
      const target = key && this.moveTargetsByKey ? this.moveTargetsByKey.get(key) : null
      if (target && !target.disabled) {
        this.previewMoveTarget(target)
      } else {
        this.restoreDefaultMoveTargetPreview()
      }
    }
    if (key) {
      this.highlightRow(key)
    } else {
      this.clearHoveredRow()
    }
  }

  // A background click is the natural way out of passive inspection. Active
  // move/merge/split workflows are deliberately not cancelled by a miss: the
  // always-visible Back action is safer than losing work while orbiting.
  handleFleetEmptyClicked() {
    if (this.pieceSelectionActive() || this.mergeModeActive() || this.splitModeActive()) {
      this.clearFleetEmptyHint()
      return
    }
    if (this.selectedKey || this.activeFilter) {
      this.previewBack()
      return
    }
    this.showFleetEmptyHint('Click a truck or piece to select it. Drag to rotate.')
  }

  showFleetEmptyHint(message) {
    if (!this.hasFleetContainerTarget) return

    if (!this.fleetEmptyHintEl) {
      const hint = document.createElement('div')
      hint.className = 'project-load-plan__fleet-hint'
      this.fleetContainerTarget.appendChild(hint)
      this.fleetEmptyHintEl = hint
    }
    this.fleetEmptyHintEl.textContent = message
    this.fleetEmptyHintEl.classList.remove('hidden')

    if (this.fleetEmptyHintTimeout) {
      window.clearTimeout(this.fleetEmptyHintTimeout)
    }
    this.fleetEmptyHintTimeout = window.setTimeout(() => this.clearFleetEmptyHint(), 4000)
  }

  clearFleetEmptyHint() {
    if (this.fleetEmptyHintTimeout) {
      window.clearTimeout(this.fleetEmptyHintTimeout)
      this.fleetEmptyHintTimeout = null
    }
    if (this.fleetEmptyHintEl) {
      this.fleetEmptyHintEl.remove()
      this.fleetEmptyHintEl = null
    }
  }

  handleRowClick(event) {
    const target = event.target
    if (target && target.closest('button, a, form, input, select')) return

    const row = event.currentTarget
    const key = row.dataset.key
    if (!key) return

    if (this.moveCompletionPending()) {
      if (this.continueMoveFromCompletedTarget(key)) return

      // A different truck's row: apply the held result, then reopen that
      // truck on the refreshed workspace instead of swallowing the click.
      const completion = this.pendingMoveCompletion
      const willRerender = Boolean(completion && completion.body && completion.body.trim().length)
      if (willRerender) {
        window.projectLoadPlanningPendingRowSelection = this.pendingRowSelectionIdentity(
          key,
          completion.movedRfqUnitId
        )
      }
      this.flushPendingMoveCompletion()
      if (!willRerender) this.applySelectedKey(key)
      return
    }

    if (this.openMoveConfirmationForExistingTarget(key)) {
      return
    }

    if (this.pieceSelectionActive()) {
      if (String(key) === String(this.activeMoveSourceKey)) return
      this.renderMovePanelNonTarget(key)
      return
    }

    if (this.openMergeConfirmationForTarget(key)) {
      return
    }

    // The row is an open/close toggle: re-clicking the selected truck's row
    // collapses its details and returns to the previous view.
    if (this.selectedKey === key) {
      this.collapseRow(key)
      this.previewBack()
      return
    }

    if (this.activeFilter && this.activeFilter.type !== 'keys') {
      this.selectionParentFilter = this.activeFilter
    }
    this.clearMoveMode()
    this.clearSplitMode()
    this.applySelectedKey(key)
  }

  handleRowKeydown(event) {
    if (!(event.key === 'Enter' || event.key === ' ')) return

    const row = event.currentTarget.closest('[data-project-load-planning-target~="row"]')
    if (!row) return

    event.preventDefault()
    this.handleRowClick({ target: event.currentTarget, currentTarget: row })
  }

  handleLoadRowClick(event) {
    const target = event.target
    if (target && target.closest('button, a, form, input, select')) return

    const key = event.currentTarget.dataset.parentKey
    if (!key) return

    const rfqUnitId = event.currentTarget.dataset.rfqUnitId
    if (this.moveCompletionPending()) {
      if (rfqUnitId) this.continueMoveFromCompletedSource(key, rfqUnitId)
      return
    }
    if (rfqUnitId) {
      if (this.pieceSelectionActive()) {
        if (String(key) === String(this.activeMoveSourceKey)) {
          if (String(rfqUnitId) !== String(this.currentPreviewLoadRfqUnitId)) {
            this.selectLoadForMove(key, rfqUnitId)
          }
          return
        }
        if (this.openMoveConfirmationForExistingTarget(key)) return
        this.renderMovePanelNonTarget(key)
        return
      }
      this.selectLoadForMove(key, rfqUnitId)
    } else {
      this.clearMoveMode()
      this.applySelectedKey(key)
    }
  }

  handleLoadLabelClick(event) {
    const row = event.currentTarget.closest('.project-load-plan__load-row')
    if (!row) return

    event.preventDefault()
    this.handleLoadRowClick({ target: row, currentTarget: row })
  }

  handleMoveKeydown(event) {
    if (event.key !== 'Escape') return
    if (event.defaultPrevented) return
    if (this.dialogActiveForEvent(event)) return

    if (this.addTruckPopoverOpen()) {
      event.preventDefault()
      this.closeAddTruck()
      if (this.hasAddTruckButtonTarget) this.addTruckButtonTarget.focus()
      return
    }

    if (this.previewContextActive()) {
      event.preventDefault()
      this.previewBack()
    }
  }

  toggleAddTruck(event) {
    event.preventDefault()

    if (this.addTruckPopoverOpen()) {
      this.closeAddTruck()
      return
    }

    this.applyAddTruckDefaults()
    this.setupAddTruckEquipmentSearch()
    this.addTruckPopoverTarget.classList.remove('hidden')
    this.addTruckButtonTarget.setAttribute('aria-expanded', 'true')

    window.requestAnimationFrame(() => {
      const routeNeedsChoice = this.hasAddTruckRouteTarget && this.addTruckRouteTarget.tagName === 'SELECT' && !this.addTruckRouteTarget.value
      const focusTarget = routeNeedsChoice ? this.addTruckRouteTarget : this.addTruckEquipmentSearchTarget
      if (focusTarget) focusTarget.focus()
      if (!routeNeedsChoice) this.openAddTruckEquipmentSearch()
    })
  }

  keepAddTruckOpen(event) {
    event.stopPropagation()
  }

  handleAddTruckOutsideClick(event) {
    if (!this.addTruckPopoverOpen()) return
    if (this.addTruckButtonTarget.contains(event.target)) return
    if (this.addTruckPopoverTarget.contains(event.target)) return
    if (event.target && typeof event.target.closest === 'function' && event.target.closest('.project-load-plan__add-truck-autocomplete')) return

    this.closeAddTruck()
  }

  handleUnplannedEquipmentSearchFocus(event) {
    const target = event.target && typeof event.target.closest === 'function'
      ? event.target.closest('[data-project-load-planning-equipment-search="true"]')
      : null
    if (!target) return

    const workspace = this.element.closest('#project-load-management-workspace')
    if (!workspace || target.closest('#project-load-management-workspace') !== workspace) return
    if (target.dataset.equipmentOptionsLoaded === 'true' || target.dataset.equipmentOptionsLoading === 'true') return

    this.loadUnplannedEquipmentOptions(target)
  }

  async loadUnplannedEquipmentOptions(input) {
    const optionsUrl = input && input.dataset ? input.dataset.optionsUrl : null
    if (!optionsUrl) return

    input.dataset.equipmentOptionsLoading = 'true'
    input.setAttribute('aria-busy', 'true')
    input.readOnly = true
    const originalPlaceholder = input.getAttribute('placeholder') || 'Choose equipment'
    input.setAttribute('placeholder', 'Checking equipment…')

    try {
      const response = await fetch(optionsUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!response.ok) throw new Error('Equipment options are unavailable.')

      const payload = await response.json()
      if (payload && payload.error) throw new Error(payload.error)

      const options = this.unplannedEquipmentSearchOptions(payload)
      const appLoadPlanning = window.App && window.App.loadPlanning
      if (!(window.jQuery && appLoadPlanning && typeof appLoadPlanning.setupEquipmentOptionSearch === 'function')) {
        throw new Error('Equipment options are unavailable.')
      }

      const $input = window.jQuery(input)
      $input.data('options', options)
      input.dataset.equipmentOptionsLoaded = 'true'
      input.setAttribute('data-behavior', 'load-planning-equipment-option-search')
      appLoadPlanning.setupEquipmentOptionSearch($input)
      $input.autocomplete('option', 'position', {
        my: 'right top',
        at: 'right bottom',
        collision: 'fit',
      })

      if (!options.length) {
        input.setAttribute('placeholder', 'No saved equipment available')
        return
      }

      $input.select()
      $input.autocomplete('search', '')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Equipment options are unavailable.'
      if (window.Alert && typeof window.Alert.show === 'function') {
        window.Alert.show(message, 'warning')
      }
    } finally {
      delete input.dataset.equipmentOptionsLoading
      input.removeAttribute('aria-busy')
      input.readOnly = false
      if (input.dataset.equipmentOptionsLoaded !== 'true' || input.getAttribute('placeholder') === 'Checking equipment…') {
        input.setAttribute('placeholder', originalPlaceholder)
      }
    }
  }

  unplannedEquipmentSearchOptions(payload) {
    const rawOptions = payload && Array.isArray(payload.new_trailer_options) ? payload.new_trailer_options : []
    // Per-truck equipment switching reuses this mapping; source.piece_count is
    // only present there and gates the multi-piece "keep what fits" fork.
    const pieceCount = Number(payload && payload.source && payload.source.piece_count) || 0
    const options = rawOptions.map((option) => {
      const status = String(option && option.status || '')
      const current = Boolean(option && option.current)
      const actionable = Boolean(option && !option.disabled && option.equipment_candidate_key) && !current
      const outcomeLabels = {
        clear: 'Runs legal',
        permit: 'Permit needed',
        blocked: "Won't fit",
      }
      const label = option && option.label ? option.label : 'Equipment'
      const disabledReason = option && option.reason
        ? option.reason
        : 'This equipment does not work for this piece.'
      const keepFittingPrompt = !current && status === 'blocked' && pieceCount > 1 && option && option.equipment_candidate_key
        ? `${label} can't take all ${pieceCount} pieces: ${disabledReason}.\n\nKeep the pieces that fit and move the rest to the unassigned pieces list?`
        : null

      return {
        label,
        value: label,
        search: option && option.search,
        outcome_label: outcomeLabels[status] || (option && option.status_label) || 'Unavailable',
        detail_label: (option && (option.reason || option.recommendation_reason)) || '',
        disabled_reason: disabledReason,
        recommended: false,
        saved: current,
        actionable,
        overridable: false,
        keep_fitting_prompt: keepFittingPrompt,
        candidate_key: option && option.equipment_candidate_key,
      }
    })
    const recommendedIndex = options.findIndex((option) => option.actionable)
    if (recommendedIndex >= 0) options[recommendedIndex].recommended = true

    return options
  }

  closeAddTruck() {
    if (!this.hasAddTruckPopoverTarget) return

    this.closeAddTruckEquipmentSearch()
    this.addTruckPopoverTarget.classList.add('hidden')
    if (this.hasAddTruckButtonTarget) this.addTruckButtonTarget.setAttribute('aria-expanded', 'false')
  }

  addTruckPopoverOpen() {
    return this.hasAddTruckPopoverTarget && !this.addTruckPopoverTarget.classList.contains('hidden')
  }

  applyAddTruckDefaults() {
    const selectedItem = this.itemsByKey && this.selectedKey ? this.itemsByKey.get(this.selectedKey) : null
    if (!selectedItem) return

    this.selectAddTruckOption(this.hasAddTruckRouteTarget ? this.addTruckRouteTarget : null, selectedItem.lane_key)
    this.selectAddTruckEquipment(selectedItem.tractor_trailer_id)
  }

  selectAddTruckOption(control, value) {
    if (!control || value == null || control.tagName !== 'SELECT') return

    const normalizedValue = String(value)
    if (Array.from(control.options).some((option) => option.value === normalizedValue)) {
      control.value = normalizedValue
    }
  }

  addTruckEquipmentSearchOptions() {
    return (Array.isArray(this.newTrailerOptions) ? this.newTrailerOptions : [])
      .filter((option) => option && option.tractor_trailer_id != null && option.label)
      .map((option) => ({
        label: String(option.label),
        value: String(option.label),
        search: option.search,
        tractor_trailer_id: String(option.tractor_trailer_id),
      }))
  }

  setupAddTruckEquipmentSearch() {
    if (!this.hasAddTruckEquipmentSearchTarget || !this.hasAddTruckEquipmentTarget) return
    if (!(window.jQuery && window.jQuery.fn && window.jQuery.fn.autocomplete)) return

    const input = this.addTruckEquipmentSearchTarget
    const $input = window.jQuery(input)
    if ($input.data('project-load-plan-add-truck-search-ready')) return

    const options = this.addTruckEquipmentSearchOptions()
    $input.data('project-load-plan-add-truck-search-ready', true)
    $input.on('focus.projectLoadPlanAddTruck', () => {
      this.openAddTruckEquipmentSearch()
      input.select()
    })
    $input.on('click.projectLoadPlanAddTruck', () => {
      if (!$input.autocomplete('widget').is(':visible')) this.openAddTruckEquipmentSearch()
    })
    $input.on('input.projectLoadPlanAddTruck', () => {
      const selected = options.find((option) => (
        option.tractor_trailer_id === String(this.addTruckEquipmentTarget.value || '') &&
        option.label === input.value
      ))
      if (selected) return

      this.addTruckEquipmentTarget.value = ''
      input.setCustomValidity('Choose equipment from the list.')
    })
    $input.autocomplete({
      minLength: 0,
      delay: 0,
      source: (request, response) => {
        const term = String(request.term || '').trim()
        const matches = window.EquipmentSearch.filter(options, term)
        window.EquipmentSearch.feedback(input, matches, term)
        response(matches)
        window.EquipmentSearch.observe(input, matches, term)
      },
      focus: (event, ui) => {
        event.preventDefault()
        input.value = ui.item.label
      },
      select: (event, ui) => {
        event.preventDefault()
        window.EquipmentSearch.selected(input, ui.item)
        this.selectAddTruckEquipment(ui.item.tractor_trailer_id)
      },
      change: (_event, ui) => {
        if (ui.item) return

        const selected = options.find((option) => option.tractor_trailer_id === String(this.addTruckEquipmentTarget.value || ''))
        input.value = selected ? selected.label : ''
        input.setCustomValidity(selected ? '' : 'Choose equipment from the list.')
        window.EquipmentSearch.clearFeedback(input)
      },
    })

    const instance = $input.autocomplete('instance') || $input.data('ui-autocomplete') || $input.data('autocomplete')
    if (!instance) return

    instance._renderItem = (ul, item) => {
      const $content = window.jQuery('<div class="ui-menu-item-wrapper"></div>').text(item.label)
      return window.jQuery('<li></li>').append($content).appendTo(ul)
    }
    instance._renderMenu = (ul, items) => {
      window.jQuery(ul).addClass('equipment-selector-autocomplete project-load-plan__add-truck-autocomplete')
      items.forEach((item) => instance._renderItemData(ul, item))
    }
  }

  selectAddTruckEquipment(value) {
    if (!this.hasAddTruckEquipmentSearchTarget || !this.hasAddTruckEquipmentTarget || value == null) return

    const selected = this.addTruckEquipmentSearchOptions().find((option) => option.tractor_trailer_id === String(value))
    if (!selected) return

    this.addTruckEquipmentTarget.value = selected.tractor_trailer_id
    this.addTruckEquipmentSearchTarget.value = selected.label
    this.addTruckEquipmentSearchTarget.setCustomValidity('')
  }

  openAddTruckEquipmentSearch() {
    if (!this.hasAddTruckEquipmentSearchTarget || !(window.jQuery && window.jQuery.fn && window.jQuery.fn.autocomplete)) return

    const $input = window.jQuery(this.addTruckEquipmentSearchTarget)
    if (!$input.data('project-load-plan-add-truck-search-ready')) return
    const value = this.addTruckEquipmentSearchTarget.value || ''
    const selected = this.addTruckEquipmentSearchOptions().find((option) => (
      option.tractor_trailer_id === String(this.addTruckEquipmentTarget.value || '') && option.label === value
    ))
    $input.autocomplete('search', selected ? '' : value)
  }

  closeAddTruckEquipmentSearch() {
    if (!this.hasAddTruckEquipmentSearchTarget || !(window.jQuery && window.jQuery.fn && window.jQuery.fn.autocomplete)) return

    const $input = window.jQuery(this.addTruckEquipmentSearchTarget)
    if (!$input.data('project-load-plan-add-truck-search-ready')) return
    $input.autocomplete('close')
  }

  destroyAddTruckEquipmentSearch() {
    if (!this.hasAddTruckEquipmentSearchTarget || !(window.jQuery && window.jQuery.fn && window.jQuery.fn.autocomplete)) return

    const $input = window.jQuery(this.addTruckEquipmentSearchTarget)
    if (!$input.data('project-load-plan-add-truck-search-ready')) return
    $input.off('.projectLoadPlanAddTruck')
    $input.autocomplete('destroy')
  }

  dialogActiveForEvent(event) {
    const target = event && event.target
    if (target && typeof target.closest === 'function' && target.closest('.modal')) return true
    if (document.body && document.body.classList.contains('modal-open')) return true

    const dialogs = [
      this.hasQuoteConfirmModalTarget ? this.quoteConfirmModalTarget : null,
      this.hasModalTarget ? this.modalTarget : null,
    ].filter(Boolean)
    return dialogs.some((dialog) => (
      dialog.classList.contains('in') ||
      dialog.classList.contains('show') ||
      dialog.style.display === 'block'
    ))
  }

  previewBack(event) {
    if (event) event.preventDefault()
    const restoreViewerFocus = Boolean(
      this.hasPreviewBackTarget && this.previewBackTarget === document.activeElement
    )
    this.clearFleetEmptyHint()

    if (this.moveCompletionPending()) {
      const completion = this.pendingMoveCompletion
      const willRerender = Boolean(completion && completion.body && completion.body.trim().length)
      window.projectLoadPlanningPendingRowSelection = null
      window.projectLoadPlanningPendingFleetView = willRerender ? true : null
      this.flushPendingMoveCompletion()
      if (!willRerender) {
        this.clearMoveMode()
        this.clearSelectedKey()
        this.selectionParentFilter = null
        if (this.activeFilter) this.clearFilter()
        this.syncComparisonPreview()
        this.restoreViewerFocusAfterBack(restoreViewerFocus)
      }
      return
    }

    if (this.pieceSelectionActive()) {
      const returnFilter = this.moveReturnFilter
      this.clearMoveMode()
      this.clearSelectedKey()
      this.selectionParentFilter = null
      if (returnFilter) {
        this.setFilter(returnFilter)
      } else {
        this.clearFilter()
      }
      this.syncComparisonPreview()
      this.restoreViewerFocusAfterBack(restoreViewerFocus)
      return
    }

    if (this.mergeModeActive()) {
      this.clearMergeMode()
      if (this.activeFilter) {
        this.applyFilter()
      } else {
        this.syncPreviewContext()
        this.fitPreviewView('auto')
      }
      this.syncComparisonPreview()
      this.restoreViewerFocusAfterBack(restoreViewerFocus)
      return
    }

    if (this.splitModeActive()) {
      this.clearSplitMode()
      if (this.activeFilter) {
        this.applyFilter()
      } else {
        this.syncPreviewContext()
        this.fitPreviewView('auto')
      }
      this.syncComparisonPreview()
      this.restoreViewerFocusAfterBack(restoreViewerFocus)
      return
    }

    if (this.selectedKey) {
      const keyFilterActive = this.activeFilter && this.activeFilter.type === 'keys'
      const parentFilter = this.selectionParentFilter
      this.selectionParentFilter = null
      this.clearSelectedKey()
      this.hideMovePanel()

      if (keyFilterActive) {
        if (parentFilter) {
          this.setFilter(parentFilter)
        } else {
          this.clearFilter()
        }
      } else if (this.activeFilter) {
        // Operational modes temporarily restore the whole fleet so compatible
        // destinations cannot disappear behind a summary filter. Returning to
        // that filter also restores its grouped 3D layout.
        this.applyFilter()
      } else {
        this.syncPreviewContext()
        this.fitPreviewView('auto')
      }
      this.syncComparisonPreview()
      this.restoreViewerFocusAfterBack(restoreViewerFocus)
      return
    }

    // Side-by-side comparison is driven by the checked rows, so leaving it
    // through the viewer's back control also clears that selection.
    if (this.comparisonPreviewActive()) {
      this.clearBulkSelection()
      this.restoreViewerFocusAfterBack(restoreViewerFocus)
      return
    }

    if (this.activeFilter) {
      this.selectionParentFilter = null
      this.clearFilter()
    }
    this.restoreViewerFocusAfterBack(restoreViewerFocus)
  }

  restoreViewerFocusAfterBack(shouldRestore) {
    if (!shouldRestore || this.previewContextActive() || !this.hasFleetContainerTarget) return

    const viewer = this.fleetContainerTarget.closest('.project-load-plan__fleet-panel-body')
    if (!(viewer && typeof viewer.focus === 'function')) return

    try {
      viewer.focus({ preventScroll: true })
    } catch (_error) {
      viewer.focus()
    }
  }

  scrollToPricedQuoteRow(key) {
    const quoteId = String(key).startsWith('quote:') ? String(key).slice('quote:'.length) : null
    if (!quoteId) return

    const row = document.querySelector(`tr[data-behavior~="project-rfqs-quote-parent-row"][data-quote-id="${quoteId}"]`)
    if (!(row && typeof row.scrollIntoView === 'function')) return

    row.scrollIntoView({ behavior: 'smooth', block: 'center' })
    row.classList.add('is-flash-highlighted')
    window.setTimeout(() => row.classList.remove('is-flash-highlighted'), 1600)
  }

  handleRowMouseEnter(event) {
    const key = event.currentTarget.dataset.key
    if (!key) return

    this.highlightRow(key)
    if (this.fleetRenderer && typeof this.fleetRenderer.hoverItem === 'function') {
      this.fleetRenderer.hoverItem(key)
    }
  }

  handleRowMouseLeave() {
    this.clearHoveredRow()
    if (this.fleetRenderer && typeof this.fleetRenderer.hoverItem === 'function') {
      this.fleetRenderer.hoverItem(null)
    }
  }

  expandRow(key) {
    if (!key) return

    const expanded = this.expandedKeysSet()
    if (!expanded.has(key)) {
      expanded.add(key)
      this.writeExpandedKeys(expanded)
      this.applyExpansionToDom()
    }
  }

  collapseRow(key) {
    if (!key) return

    const expanded = this.expandedKeysSet()
    if (!expanded.has(key)) return

    expanded.delete(key)
    this.writeExpandedKeys(expanded)
    this.applyExpansionToDom()
  }

  ensureRowExpanded(key) {
    this.expandRow(key)
  }

  applyExpansionToDom() {
    const expanded = this.expandedKeysSet()

    this.rowTargets.forEach((row) => {
      const key = row.dataset.key
      const isExpanded = expanded.has(key)

      row.setAttribute('aria-expanded', isExpanded ? 'true' : 'false')
      const equipmentAction = row.querySelector('.project-load-plan__row-cell--equipment[role="button"]')
      if (equipmentAction) {
        equipmentAction.setAttribute('aria-expanded', isExpanded ? 'true' : 'false')
      }
    })

    this.detailTargets.forEach((detail) => {
      const key = detail.dataset.parentKey
      detail.classList.toggle('hidden', !expanded.has(key))
    })

    if (this.hasRowGroupTarget) {
      this.rowGroupTargets.forEach((group) => {
        group.classList.toggle('is-expanded', expanded.has(group.dataset.key))
      })
    }
  }

  applySelectedKey(key, { preserveLoadSelection = false, skipExpansion = false } = {}) {
    if (!key || !this.itemsByKey.has(key)) return

    // Something got selected — the orientation nudge is no longer relevant.
    this.clearFleetEmptyHint()

    if (!skipExpansion) {
      this.ensureRowExpanded(key)
    }

    const keyChanged = this.selectedKey !== key
    this.selectedKey = key
    if (keyChanged && !preserveLoadSelection) {
      this.currentPreviewLoadRfqUnitId = null
    }

    if (this.fleetRenderer) {
      this.fleetRenderer.selectItem(key)
    }
    this.setPreviewSceneContext([key], { minimalLabels: true })

    this.syncRowSelection()
    this.syncSelectionDataset()
    this.syncPreviewContext()
  }

  clearSelectedKey() {
    this.selectedKey = null
    this.currentPreviewLoadRfqUnitId = null
    this.clearPreviewSceneContext()
    if (this.fleetRenderer) {
      this.fleetRenderer.selectItem(null)
    }
    this.syncRowSelection()
    this.syncSelectionDataset()
    this.syncPreviewContext()
  }

  syncRowSelection() {
    this.rowTargets.forEach((row) => {
      row.classList.toggle('is-selected', row.dataset.key === this.selectedKey)
      row.classList.toggle(
        'is-bulk-selected',
        row.dataset.selectable === 'true' && this.selectedGroupKeys.has(row.dataset.groupKey)
      )
    })

    if (this.hasRowGroupTarget) {
      this.rowGroupTargets.forEach((group) => {
        const isSelected = group.dataset.key === this.selectedKey
        const isBulkSelected =
          group.dataset.selectable === 'true' && this.selectedGroupKeys.has(group.dataset.groupKey)

        group.classList.toggle('is-selected', isSelected)
        group.classList.toggle('is-bulk-selected', isBulkSelected)
        group.dataset.selectedPanelState = isSelected ? 'selected' : 'idle'
      })
    }
  }

  syncSelectionDataset() {
    if (this.selectedKey) {
      this.element.dataset.selectedKey = this.selectedKey
    } else {
      delete this.element.dataset.selectedKey
    }

    if (this.currentPreviewLoadRfqUnitId) {
      this.element.dataset.selectedLoadRfqUnitId = String(this.currentPreviewLoadRfqUnitId)
    } else {
      delete this.element.dataset.selectedLoadRfqUnitId
    }
  }

  pieceSelectionActive() {
    return Boolean(this.activeMoveSourceKey && this.currentPreviewLoadRfqUnitId)
  }

  moveCompletionPending() {
    return Boolean(this.pendingMoveCompletion)
  }

  previewContextActive() {
    return Boolean(
      this.pieceSelectionActive() ||
      this.mergeModeActive() ||
      this.splitModeActive() ||
      this.selectedKey ||
      this.activeFilter ||
      this.comparisonPreviewActive()
    )
  }

  syncPreviewContext({ status } = {}) {
    const state = this.previewStateText()
    let nextStatus = status
    if (nextStatus === undefined) {
      if (this.pieceSelectionActive()) {
        // The compact decision strip below the scene carries move consequences.
        // Repeating it as a floating canvas message obscures the rigs.
        nextStatus = ''
      } else if (this.mergeModeActive() || this.splitModeActive()) {
        nextStatus = this.hasMovePanelStatusTarget ? this.movePanelStatusTarget.textContent.trim() : ''
      } else if (this.selectedKey) {
        nextStatus = this.selectedTruckStatus(this.selectedKey)
      } else {
        nextStatus = ''
      }
    }

    if (this.hasPreviewStateTarget) {
      this.previewStateTarget.textContent = state
      this.previewStateTarget.classList.toggle('hidden', !state)
      this.previewStateTarget.setAttribute('aria-hidden', state ? 'false' : 'true')
    }
    if (this.hasPreviewStatusTarget) {
      this.previewStatusTarget.textContent = nextStatus || ''
      this.previewStatusTarget.classList.toggle('hidden', !nextStatus)
    }
    if (this.hasPreviewBackTarget) {
      this.previewBackTarget.classList.toggle('hidden', !this.previewContextActive())
      const exitLabel = this.previewExitLabel()
      this.previewBackTarget.setAttribute('aria-label', exitLabel)
      if (this.hasPreviewBackLabelTarget) this.previewBackLabelTarget.textContent = exitLabel
    }
  }

  previewExitLabel() {
    if (this.moveCompletionPending()) return 'All trucks'
    if (this.pieceSelectionActive()) {
      const item = this.itemsByKey && this.itemsByKey.get(this.activeMoveSourceKey)
      return item && item.source_type === 'unplanned_load' ? 'Cancel assignment' : 'Put back'
    }
    if (this.mergeModeActive()) return 'Cancel merge'
    if (this.splitModeActive()) return this.unstackModeActive() ? 'Cancel unstack' : 'Cancel split'
    return 'All trucks'
  }

  previewStateText() {
    if (this.moveCompletionPending()) {
      // The contextual action bar below the scene is the single completion
      // surface. Repeating the same result over the canvas obscures the rigs.
      return ''
    }
    if (this.pieceSelectionActive()) {
      const item = this.itemsByKey && this.itemsByKey.get(this.activeMoveSourceKey)
      const assigning = item && item.source_type === 'unplanned_load'
      const label = this.loadDisplayLabel(this.activeMoveSourceKey, this.currentPreviewLoadRfqUnitId)
      const vitals = this.loadVitalsText(this.activeMoveSourceKey, this.currentPreviewLoadRfqUnitId)
      return `${assigning ? 'Assigning' : 'Moving'}: ${label}${vitals ? ` · ${vitals}` : ''}`
    }
    if (this.mergeModeActive()) return 'Merging selected trucks'
    if (this.splitModeActive()) return this.unstackModeActive() ? 'Unstacking selected truck' : 'Splitting selected truck'
    if (this.selectedKey) return this.truckDisplayLabel(this.selectedKey)
    if (this.comparisonPreviewActive()) {
      const count = this.comparisonPreviewKeys().length
      return `Comparing ${count} trucks · click a piece to move it`
    }
    if (this.activeFilter) return `Viewing ${this.activeFilter.label || 'filtered equipment'}`
    return ''
  }

  truckDisplayLabel(key) {
    const item = this.itemsByKey && this.itemsByKey.get(key)
    if (!item) return 'Selected truck'

    const metadata = item.metadata || {}
    const title = metadata.equipment_name || metadata.title || item.description || 'Truck'
    const fleetKeys = this.fleetItemsByKey ? Array.from(this.fleetItemsByKey.keys()) : []
    const index = fleetKeys.indexOf(key)
    return index >= 0 ? `Truck ${index + 1} · ${title}` : title
  }

  selectedTruckStatus(key) {
    const item = this.itemsByKey && this.itemsByKey.get(key)
    const metadata = (item && item.metadata) || {}
    const route = metadata.origin && metadata.destination
      ? `${metadata.origin} → ${metadata.destination}`
      : null
    return [route, this.truckVitalsText(item)].filter(Boolean).join(' · ')
  }

  formatWeight(value) {
    const numeric = Number(value)
    if (!(Number.isFinite(numeric) && numeric > 0)) return null
    return `${Math.round(numeric).toLocaleString('en-US')} lb`
  }

  utilizationPercent(value) {
    const numeric = Number(value)
    if (!(Number.isFinite(numeric) && numeric >= 0)) return null
    const percent = numeric <= 1.5 ? numeric * 100 : numeric
    return `${Math.round(percent)}%`
  }

  truckVitalsText(itemOrSummary) {
    const source = itemOrSummary || {}
    const metadata = source.metadata || source
    const pieceCount = Number(metadata.piece_count != null ? metadata.piece_count : metadata.load_count)
    const gross = this.formatWeight(
      metadata.gross_weight_lb != null ? metadata.gross_weight_lb : metadata.total_weight_lb
    )
    const axle = this.utilizationPercent(metadata.max_axle_utilization)
    const governing = metadata.governing_reason_label || metadata.governing_axle_label
    const parts = []
    if (Number.isFinite(pieceCount)) parts.push(`${pieceCount} ${pieceCount === 1 ? 'piece' : 'pieces'}`)
    if (gross) parts.push(`${gross} gross`)
    if (axle) parts.push(`${governing || 'governing axle'} ${axle}`)
    return parts.join(' · ')
  }

  loadDisplayLabel(key, rfqUnitId) {
    const item = this.itemsByKey && this.itemsByKey.get(key)
    const rows = item && Array.isArray(item.load_rows) ? item.load_rows : []
    const index = rows.findIndex((row) => String(row.rfq_unit_id) === String(rfqUnitId))
    const row = index >= 0 ? rows[index] : null
    const rawDescription = (row && row.description) || (item && item.description) || 'selected piece'
    const description = String(rawDescription).replace(/^\(\s*\d+\s*ea\s*\)\s*/i, '')
    return index >= 0 ? `Piece ${index + 1} of ${rows.length} · ${description}` : description
  }

  targetDisplayLabel(target) {
    if (!(target && target.label)) return 'Selected truck'
    if (!(target.kind === 'draft' && target.key && this.fleetItemsByKey)) return target.label

    const index = Array.from(this.fleetItemsByKey.keys()).indexOf(target.key)
    return index >= 0 ? `Truck ${index + 1} · ${target.label}` : target.label
  }

  targetChoiceLabel(target) {
    if (target && target.kind === 'draft' && target.key && this.fleetItemsByKey && this.fleetItemsByKey.has(target.key)) {
      return this.truckDisplayLabel(target.key)
    }
    return (target && target.label) || 'Selected truck'
  }

  // Stack/unstack targets carry their own key ("stack:<group>:<unit>") plus a
  // scene_key naming the truck item the 3D marker should attach to.
  targetSceneKey(target) {
    return (target && (target.scene_key || target.key)) || null
  }

  stackTargetFor(sceneKey, rfqUnitId) {
    if (!(this.moveTargetsByKey && sceneKey && rfqUnitId)) return null
    for (const target of this.moveTargetsByKey.values()) {
      if (
        target &&
        target.kind === 'stack' &&
        !target.disabled &&
        String(this.targetSceneKey(target)) === String(sceneKey) &&
        String(target.stack_on_rfq_unit_id) === String(rfqUnitId)
      ) return target
    }
    return null
  }

  // Compact "Stack on <piece> · <truck>" text: the server label carries the
  // full equipment spec, which is too loud for chips and 3D sprites.
  stackTargetLabel(target, { includeTruck = true } = {}) {
    if (target.kind === 'unstack') return target.label || 'Put back on the deck'
    const sceneKey = this.targetSceneKey(target)
    const item = sceneKey && this.itemsByKey && this.itemsByKey.get(sceneKey)
    const rows = item && Array.isArray(item.load_rows) ? item.load_rows : []
    const baseIsVisible = rows.some((row) => String(row.rfq_unit_id) === String(target.stack_on_rfq_unit_id))
    const piece = baseIsVisible
      ? this.loadDisplayLabel(sceneKey, target.stack_on_rfq_unit_id)
      : (target.stack_on_label || 'this piece')
    if (!includeTruck) return `Stack on ${piece}`
    const sameTruck = String(sceneKey) === String(this.activeMoveSourceKey)
    const truckLabel = sameTruck
      ? 'this truck'
      : (sceneKey && this.fleetItemsByKey && this.fleetItemsByKey.has(sceneKey) ? this.truckDisplayLabel(sceneKey) : null)
    return [`Stack on ${piece}`, truckLabel].filter(Boolean).join(' · ')
  }

  targetSceneLabel(target) {
    if (!(target && this.targetSceneKey(target) && this.fleetItemsByKey)) return 'Truck'
    if (target.kind === 'stack' || target.kind === 'unstack') return this.stackTargetLabel(target, { includeTruck: false })
    const index = Array.from(this.fleetItemsByKey.keys()).indexOf(target.key)
    const truckNumber = index >= 0 ? `Truck ${index + 1}` : 'Truck'
    const truck = target.truck || {}
    const item = this.itemsByKey && this.itemsByKey.get(target.key)
    const metadata = (item && item.metadata) || {}
    const equipment = metadata.equipment_name || truck.equipment_label || this.equipmentTypeLabel(target)
    const projected = truck.projected || {}
    const vitals = this.truckVitalsText(projected)
    return [truckNumber, equipment, vitals ? `After move: ${vitals}` : null].filter(Boolean).join(' · ')
  }

  sourceSceneLabel(key, source = {}) {
    const item = this.itemsByKey && this.itemsByKey.get(key)
    const truck = source.truck || {}
    const current = truck.current || (item && item.metadata) || {}
    const vitals = this.truckVitalsText(current)
    return [
      'Current',
      this.truckDisplayLabel(key),
      vitals,
    ].filter(Boolean).join(' · ')
  }

  equipmentTypeLabel(target) {
    const label = String((target && target.label) || 'new equipment').trim()
    const concise = label.split(/\s+\d+'\d+"/)[0].trim()
    return concise || label
  }

  fitPreviewView(mode = 'auto') {
    if (this.fleetRenderer && typeof this.fleetRenderer.fitView === 'function') {
      this.fleetRenderer.fitView(mode)
    }
  }

  navigatePreviewCamera(event) {
    if (event) event.preventDefault()
    const action = event && event.currentTarget
      ? event.currentTarget.dataset.cameraAction
      : null
    if (
      action &&
      this.fleetRenderer &&
      typeof this.fleetRenderer.navigateView === 'function'
    ) {
      this.fleetRenderer.navigateView(action)
    }
  }

  setPreviewSceneContext(keys, options = {}) {
    if (this.fleetRenderer && typeof this.fleetRenderer.setSceneContext === 'function') {
      return this.fleetRenderer.setSceneContext(keys, options)
    }
    return []
  }

  clearPreviewSceneContext() {
    if (this.fleetRenderer && typeof this.fleetRenderer.clearSceneContext === 'function') {
      this.fleetRenderer.clearSceneContext()
    }
  }

  ensureOperationalPreview() {
    if (
      this.currentPreviewRenderSignature === 'base' ||
      !(this.fleetRenderer && typeof this.fleetRenderer.render === 'function')
    ) return

    const items = this.fleetPreparedItems || []
    const unplannedItems = this.baseUnplannedItems || []
    const availableKeys = new Set([...items, ...unplannedItems].map((item) => item.key))
    const rendererSelectedKey = this.selectedKey && availableKeys.has(this.selectedKey)
      ? this.selectedKey
      : null

    this.currentPreviewRenderSignature = 'base'
    this.fleetRenderer.render({
      items,
      unplannedItems,
      selectedKey: rendererSelectedKey,
      autoSelect: false,
      showScaleFigure: true,
      clusterLabels: [],
    })
  }

  expandedKeysSet() {
    const raw = this.element.dataset.expandedKeys || ''
    return new Set(raw.split(',').map((entry) => entry.trim()).filter(Boolean))
  }

  writeExpandedKeys(set) {
    const keys = Array.from(set).filter(Boolean)
    if (keys.length) {
      this.element.dataset.expandedKeys = keys.join(',')
    } else {
      delete this.element.dataset.expandedKeys
    }
  }

  // Filtered-out rows are not selectable: every consumer (select-all, bulk
  // counts, indeterminate state, posted group keys) sees the same narrowed
  // scope, so bulk actions can never touch trucks hidden by the active filter.
  selectableRows() {
    if (!this.hasRowTarget) return []

    return this.rowTargets.filter((row) => {
      if (row.dataset.selectable !== 'true') return false

      const group = row.closest('tbody')
      return !(group && group.classList.contains('is-filtered-out'))
    })
  }

  selectedGroupKeysInDomOrder() {
    return this.selectableRows()
      .filter((row) => this.selectedGroupKeys.has(row.dataset.groupKey))
      .map((row) => row.dataset.groupKey)
  }

  toggleRowSelection(event) {
    event.stopPropagation()
    const checkbox = event.currentTarget
    const groupKey = checkbox.dataset.groupKey
    if (!groupKey) return

    if (checkbox.checked) {
      this.selectedGroupKeys.add(groupKey)
    } else {
      this.selectedGroupKeys.delete(groupKey)
    }

    this.syncBulkSelection()
    this.stageComparisonOverInspection()
  }

  toggleSelectAll(event) {
    event.stopPropagation()
    const checkbox = event.currentTarget
    const groupKeys = this.selectableRows().map((row) => row.dataset.groupKey).filter(Boolean)

    if (checkbox.checked) {
      this.selectedGroupKeys = new Set(groupKeys)
    } else {
      this.selectedGroupKeys.clear()
    }

    this.syncBulkSelection()
    this.stageComparisonOverInspection()
  }

  syncBulkSelection() {
    const validGroupKeys = new Set(this.selectableRows().map((row) => row.dataset.groupKey).filter(Boolean))
    this.selectedGroupKeys = new Set(Array.from(this.selectedGroupKeys).filter((groupKey) => validGroupKeys.has(groupKey)))
    const currentSignature = this.selectedSelectionSignature()
    if (this.approvedQuoteSelectionSignature && this.approvedQuoteSelectionSignature !== currentSignature) {
      this.approvedQuoteSelectionSignature = null
    }

    if (this.hasRowCheckboxTarget) {
      this.rowCheckboxTargets.forEach((checkbox) => {
        checkbox.checked = this.selectedGroupKeys.has(checkbox.dataset.groupKey)
      })
    }

    this.syncRowSelection()
    this.updateSelectAllCheckbox()
    this.updateBulkPanel()
    this.syncComparisonPreview()
  }

  comparisonPreviewKeys() {
    if (!(this.selectedGroupKeys && this.selectedGroupKeys.size >= 2)) return []

    return this.selectableRows()
      .filter((row) => this.selectedGroupKeys.has(row.dataset.groupKey))
      .map((row) => row.dataset.key)
      .filter((key) => key && this.itemsByKey && this.itemsByKey.has(key))
  }

  comparisonPreviewActive() {
    return Boolean(this.comparisonSceneActive)
  }

  // Checking boxes on two or more trucks is an explicit ask to see them side
  // by side. A passive single-truck inspection yields to it; active workflows
  // (move / merge / split / held move) never do.
  stageComparisonOverInspection() {
    if (!this.selectedKey) return
    if (
      this.pieceSelectionActive() ||
      this.mergeModeActive() ||
      this.splitModeActive() ||
      this.moveCompletionPending()
    ) return
    if (this.comparisonPreviewKeys().length < 2) return

    this.clearSelectedKey()
    this.hideMovePanel()
    this.syncComparisonPreview()
  }

  clearBulkSelection() {
    this.selectedGroupKeys.clear()
    this.syncBulkSelection()
  }

  // Checking two or more trucks stages exactly those trucks side by side in
  // the 3D preview, so pieces can be compared and moved between them without
  // hunting through the whole fleet. Purely visual: the bulk panel and its
  // actions keep working off the same checkbox selection, and a piece click
  // in the staged scene drops into the normal move flow. Any engaged
  // workflow (inspection, move, merge, split, held move) takes precedence;
  // leaving it re-stages the comparison while the boxes stay checked.
  syncComparisonPreview() {
    const engaged = Boolean(
      this.selectedKey ||
      this.pieceSelectionActive() ||
      this.mergeModeActive() ||
      this.splitModeActive() ||
      this.moveCompletionPending()
    )
    const keys = engaged ? [] : this.comparisonPreviewKeys()
    if (keys.length >= 2) {
      this.comparisonSceneActive = true
      if (this.fleetRenderer) {
        this.ensureOperationalPreview()
        this.setPreviewSceneContext(keys, { compact: true, minimalLabels: true })
      }
      this.syncPreviewContext()
      return
    }

    if (!this.comparisonSceneActive) return
    this.comparisonSceneActive = false
    if (engaged) return
    if (this.activeFilter) {
      this.applyFilter()
    } else {
      this.clearPreviewSceneContext()
      this.fitPreviewView('auto')
    }
    this.syncPreviewContext()
  }

  updateSelectAllCheckbox() {
    if (!this.hasSelectAllCheckboxTarget) return

    const totalSelectable = this.selectableRows().length
    const selectedCount = this.selectedGroupKeys.size

    this.selectAllCheckboxTargets.forEach((checkbox) => {
      checkbox.disabled = totalSelectable === 0
      checkbox.checked = totalSelectable > 0 && selectedCount === totalSelectable
      checkbox.indeterminate = totalSelectable > 0 && selectedCount > 0 && selectedCount < totalSelectable
    })
  }

  updateBulkPanel() {
    if (!this.hasBulkPanelTarget) return

    const selectedCount = this.selectedGroupKeys.size
    const selectedRows = this.selectableRows().filter((row) => this.selectedGroupKeys.has(row.dataset.groupKey))
    const includesEmptyTruck = selectedRows.some((row) => Number(row.dataset.loadCount) === 0)
    if (this.hasBulkSelectedCountTarget) {
      this.bulkSelectedCountTarget.textContent = `${selectedCount} selected`
    }

    // Empty trucks can still be selected for merge/delete, but there is
    // nothing to quote until a piece has been placed on them.
    if (this.hasBulkQuoteActionTarget) {
      this.bulkQuoteActionTarget.classList.toggle('hidden', includesEmptyTruck)
    }

    // Split targets a single multi-piece truck; hide the trigger otherwise so a
    // 1-piece (or multi-truck) selection can't arm an illegal split.
    if (this.hasBulkSplitButtonTarget) {
      const splitGroupKey = this.splitEligibleGroupKey()
      this.bulkSplitButtonTarget.classList.toggle('hidden', !splitGroupKey)
      this.bulkSplitButtonTarget.textContent = this.stackedGroupKey(splitGroupKey) ? 'Unstack' : 'Split'
    }

    this.bulkPanelTarget.classList.toggle('hidden', selectedCount === 0)
  }

  splitEligibleGroupKey() {
    const selectedRows = this.selectableRows().filter((row) => this.selectedGroupKeys.has(row.dataset.groupKey))
    if (selectedRows.length !== 1) return null

    return Number(selectedRows[0].dataset.loadCount) > 1 ? selectedRows[0].dataset.groupKey : null
  }

  selectedSelectionSignature() {
    return this.selectedGroupKeysInDomOrder().join(',')
  }

  countedNoun(count, singular) {
    return `${count} ${singular}${count === 1 ? '' : 's'}`
  }

  quoteChargeConfirmMessage(selectedCount) {
    return `You are about to price ${this.countedNoun(selectedCount, 'truck')}. This may create up to ${this.countedNoun(selectedCount, 'quote')}. Identical trucks may be consolidated.`
  }

  openQuoteConfirmModal(selectedCount, form) {
    if (!(this.hasQuoteConfirmModalTarget && this.hasQuoteConfirmMessageTarget && this.hasQuoteConfirmAcceptTarget)) return false

    this.pendingQuoteConfirmForm = form
    this.pendingQuoteConfirmSignature = this.selectedSelectionSignature()
    this.quoteConfirmMessageTarget.textContent = this.quoteChargeConfirmMessage(selectedCount)
    this.quoteConfirmAcceptTarget.disabled = false
    this.showBootstrapModal(this.quoteConfirmModalTarget)
    return true
  }

  closeQuoteConfirmModal(event) {
    if (event) event.preventDefault()

    this.hideQuoteConfirmModal()
  }

  hideQuoteConfirmModal() {
    if (this.hasQuoteConfirmModalTarget) {
      this.showBootstrapModal(this.quoteConfirmModalTarget, false)
    }
    this.pendingQuoteConfirmForm = null
    this.pendingQuoteConfirmSignature = null
    if (this.hasQuoteConfirmAcceptTarget) {
      this.quoteConfirmAcceptTarget.disabled = false
    }
  }

  acceptQuoteConfirm(event) {
    event.preventDefault()
    if (!(this.pendingQuoteConfirmForm && this.pendingQuoteConfirmSignature)) return

    this.approvedQuoteSelectionSignature = this.pendingQuoteConfirmSignature
    const form = this.pendingQuoteConfirmForm
    this.hideQuoteConfirmModal()

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  /* ----- Merge mode (plays out in the 3D scene) ----- */

  openMergeModal(event) {
    event.stopPropagation()
    this.enterMergeMode()
  }

  async enterMergeMode() {
    const selectedGroupKeys = this.selectedGroupKeysInDomOrder()
    if (!selectedGroupKeys.length) return

    // No dry-run endpoint available: fall back to the legacy dropdown modal.
    if (!this.hasMergeTargetsUrlValue) {
      this.openLegacyMergeModal(selectedGroupKeys)
      return
    }

    this.clearMoveMode()
    this.clearSplitMode()
    this.ensureOperationalPreview()
    this.abortMergeTargetsRequest()
    const abortController = new AbortController()
    this.mergeTargetsAbortController = abortController
    this.activeMergePayload = null
    this.mergeTargetsByKey = new Map()
    this.resetActionBar({
      badge: 'Merge trucks',
      title: selectedGroupKeys.length === 1 ? 'Checking merge targets' : `${selectedGroupKeys.length} trucks selected`,
      status: 'Finding same-lane trucks that can receive the merged pieces.',
    })
    this.revealMovePanel()

    try {
      const url = new URL(this.mergeTargetsUrlValue, window.location.origin)
      selectedGroupKeys.forEach((groupKey) => url.searchParams.append('group_keys[]', groupKey))
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: abortController.signal,
      })
      const payload = await response.json()
      if (abortController.signal.aborted) return

      if (payload && payload.error) {
        this.renderMergePanelError(payload.error)
        return
      }
      if (!response.ok) {
        throw new Error('Merge targets are unavailable.')
      }

      this.applyMergeTargetsPayload(payload)
    } catch (error) {
      if (error && error.name === 'AbortError') return

      // Endpoint failure shouldn't strand the user: the legacy dropdown modal
      // can still complete the merge.
      this.clearMergeMode()
      if (this.activeFilter) this.applyFilter()
      this.openLegacyMergeModal(selectedGroupKeys)
    } finally {
      if (this.mergeTargetsAbortController === abortController) {
        this.mergeTargetsAbortController = null
      }
    }
  }

  applyMergeTargetsPayload(payload) {
    const targets = (Array.isArray(payload && payload.targets) ? payload.targets : [])
      .filter(Boolean)
      .map((target) => ({ ...target, highlight_label: 'Merge here' }))
    this.activeMergePayload = { ...payload, targets }
    this.mergeTargetsByKey = new Map(
      targets.filter((target) => target.key && !target.disabled).map((target) => [target.key, target])
    )
    if (this.fleetRenderer && typeof this.fleetRenderer.setMoveTargets === 'function') {
      const sourceKeys = (Array.isArray(payload && payload.sources) ? payload.sources : [])
        .map((source) => source && source.key)
        .filter(Boolean)
      this.fleetRenderer.setMoveTargets(targets, { focusKeys: sourceKeys })
    }
    this.renderMergePanel(this.activeMergePayload)
  }

  renderMergePanel(payload) {
    if (!this.hasMovePanelTarget) return

    const sources = Array.isArray(payload && payload.sources) ? payload.sources : []
    const targets = Array.isArray(payload && payload.targets) ? payload.targets : []
    const enabledTargets = targets.filter((target) => !target.disabled)
    const singleSource = sources.length === 1
    const totalPieces = sources.reduce((sum, source) => sum + Number(source.load_count || 0), 0)

    let status
    if (!enabledTargets.length) {
      status = 'No same-lane truck can receive the merged pieces.'
    } else if (singleSource) {
      status = 'Choose a highlighted truck to receive the pieces.'
    } else {
      status = 'Choose the highlighted truck that should keep the combined pieces.'
    }

    this.resetActionBar({
      badge: 'Merge trucks',
      title: singleSource ? (sources[0].label || 'Selected draft truck') : `${sources.length} trucks selected`,
      vitals: totalPieces > 0 ? `${totalPieces} ${totalPieces === 1 ? 'piece' : 'pieces'} to place` : '',
      status,
    })
    this.renderActionBarTargets(
      targets,
      (target) => this.openMergeConfirmation(target),
      { verb: 'Merge into' }
    )
    this.revealMovePanel()
  }

  renderMergePanelError(message) {
    this.activeMergePayload = { error: message || 'Merge targets are unavailable.', sources: [], targets: [] }
    this.resetActionBar({
      badge: 'Merge trucks',
      title: 'Merge unavailable',
      status: message || 'Merge targets are unavailable.',
    })
    this.revealMovePanel()
  }

  renderMergePanelNonTarget(key = null) {
    if (!this.hasMovePanelTarget) return

    const payload = this.activeMergePayload || {}
    const target = (Array.isArray(payload.targets) ? payload.targets : [])
      .find((candidate) => candidate && candidate.key === key)
    const sourceKeys = new Set(
      (Array.isArray(payload.sources) ? payload.sources : [])
        .map((source) => source && source.key)
        .filter(Boolean)
    )

    let message
    if (target && target.reason) {
      message = `That truck is unavailable: ${target.reason}`
    } else if (key && sourceKeys.has(key)) {
      message = 'That is one of the trucks being merged. Choose a highlighted destination or cancel the merge.'
    } else {
      message = 'That truck cannot receive the merged pieces. Choose a highlighted truck instead.'
    }
    this.showActionBarStatus(message)
    this.syncPreviewContext({ status: message })
    this.movePanelTarget.classList.remove('hidden')
    this.revealMovePanel()
  }

  openMergeConfirmationForTarget(key) {
    if (!this.mergeModeActive()) return false

    const target = this.mergeTargetsByKey && this.mergeTargetsByKey.get(key)
    if (!target) {
      this.renderMergePanelNonTarget(key)
      return true
    }

    this.openMergeConfirmation(target)
    return true
  }

  openMergeConfirmation(target) {
    const payload = this.activeMergePayload
    if (!(payload && target && !target.disabled)) return

    const sources = Array.isArray(payload.sources) ? payload.sources : []
    const sourceGroupKeys = sources
      .map((source) => source.group_key)
      .filter((groupKey) => groupKey && String(groupKey) !== String(target.target_group_key))
    if (!sourceGroupKeys.length) return

    const statusText = target.status_label ? ` ${target.status_label}.` : ''
    const reasonText = target.reason ? ` ${target.reason}.` : ''
    const sourcesText = sourceGroupKeys.length === 1 ? 'this draft truck' : `${sourceGroupKeys.length} draft trucks`
    this.renderConfirmModal({
      title: 'Merge Trucks',
      introText: `Merge ${sourcesText} into ${this.targetDisplayLabel(target)}?${statusText}${reasonText} Placement and permit checks run after you confirm.`,
      action: this.mergeTrucksUrlValue,
      hiddenFields: {
        source_group_keys: sourceGroupKeys,
        target_group_key: target.target_group_key,
        current_preview_key: this.selectedKey,
        current_preview_load_rfq_unit_id: this.currentPreviewLoadRfqUnitId,
      },
      submitText: 'Merge',
    })
  }

  clearMergeMode() {
    this.abortMergeTargetsRequest()
    const wasActive = Boolean(this.activeMergePayload)
    this.activeMergePayload = null
    this.mergeTargetsByKey = new Map()
    if (wasActive && !this.pieceSelectionActive() && this.fleetRenderer && typeof this.fleetRenderer.setMoveTargets === 'function') {
      this.fleetRenderer.setMoveTargets([])
    }
    if (!this.pieceSelectionActive() && !this.splitModeActive()) {
      this.hideMovePanel()
    }
  }

  mergeModeActive() {
    return Boolean(this.activeMergePayload || this.mergeTargetsAbortController)
  }

  abortMergeTargetsRequest() {
    if (this.mergeTargetsAbortController) {
      this.mergeTargetsAbortController.abort()
      this.mergeTargetsAbortController = null
    }
  }

  /* ----- Split mode ----- */

  enterSplitMode(event) {
    if (event) event.stopPropagation()

    this.clearMoveMode()
    this.clearMergeMode()

    const groupKey = this.splitEligibleGroupKey()
    if (!groupKey) {
      this.splitArmedGroupKey = null
      this.splitPanelOpen = true
      this.resetActionBar({
        badge: 'Split truck',
        title: 'Select one truck',
        status: 'Select exactly one draft truck with 2 or more pieces to split.',
      })
      this.revealMovePanel()
      return
    }

    const row = this.selectableRows().find((selectableRow) => selectableRow.dataset.groupKey === groupKey)
    const key = row && row.dataset.key
    const loadCount = row ? Number(row.dataset.loadCount) : 0
    this.splitArmedGroupKey = groupKey
    this.splitPanelOpen = true
    if (key) {
      this.applySelectedKey(key)
    }

    const item = key && this.itemsByKey ? this.itemsByKey.get(key) : null
    const metadata = (item && item.metadata) || {}
    const unstacking = this.stackedGroupKey(this.selectedDraftGroupKey(key))
    this.resetActionBar({
      badge: unstacking ? 'Unstack pieces' : 'Split truck',
      title: metadata.title || metadata.equipment_name || 'Selected draft truck',
      vitals: loadCount > 0 ? `${loadCount} pieces` : '',
      status: unstacking
        ? 'Return each stacked piece to its own draft truck.'
        : 'Split every piece onto its own truck, or click a piece on the truck to move just that one off.',
    })

    const splitButton = document.createElement('button')
    splitButton.type = 'button'
    splitButton.className = 'btn btn-primary btn-sm'
    splitButton.textContent = unstacking
      ? `Unstack into ${loadCount} trucks`
      : (loadCount > 0 ? `Split into ${loadCount} trucks` : 'Split truck')
    if (this.hasBulkSplitActionTarget) {
      this.bulkSplitActionTarget.dataset.confirm = unstacking
        ? 'Unstack these pieces into singleton draft trucks?'
        : 'Split the selected draft truck into singleton trucks?'
    }
    splitButton.addEventListener('click', () => this.submitSplitForm())
    this.newTruckOptionsTarget.appendChild(splitButton)
    this.revealMovePanel()
  }

  submitSplitForm() {
    if (!this.hasBulkSplitActionTarget) return

    const form = this.bulkSplitActionTarget
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  clearSplitMode() {
    if (!this.splitPanelOpen) {
      this.splitArmedGroupKey = null
      return
    }

    this.splitPanelOpen = false
    this.splitArmedGroupKey = null
    if (!this.pieceSelectionActive() && !this.mergeModeActive()) {
      this.hideMovePanel()
    }
  }

  splitModeActive() {
    return Boolean(this.splitPanelOpen)
  }

  // Split mode doubles as Unstack for a manually stacked truck; every piece of
  // chrome (badge, preview text, cancel button) should say so consistently.
  unstackModeActive() {
    return this.splitModeActive() && this.stackedGroupKey(this.selectedDraftGroupKey(this.selectedKey))
  }

  stackedGroupKey(groupKey) {
    if (!groupKey) return false
    const item = this.itemForGroupKey(groupKey)
    if (item && item.metadata && item.metadata.stacked === true) return true
    const row = this.selectableRows().find((entry) => entry.dataset.groupKey === groupKey)
    return Boolean(row && row.dataset.stacked === 'true')
  }

  /* ----- Legacy merge modal (fallback when merge targets are unavailable) ----- */

  openLegacyMergeModal(selectedGroupKeys) {
    if (!selectedGroupKeys.length) return

    if (selectedGroupKeys.length === 1) {
      this.openSingleSelectionMergeModal(selectedGroupKeys[0])
      return
    }

    this.openMultiSelectionMergeModal(selectedGroupKeys)
  }

  openSingleSelectionMergeModal(sourceGroupKey) {
    const sourceItem = this.itemForGroupKey(sourceGroupKey)
    if (!sourceItem) return

    const options = this.buildSingleMergeOptions(sourceItem)
    this.renderModal({
      title: 'Merge Truck',
      introText: `Pick a target draft truck to merge "${(sourceItem.metadata && sourceItem.metadata.title) || 'this draft truck'}" into. Placement and permit checks run after you confirm.`,
      action: this.mergeTrucksUrlValue,
      hiddenFields: {
        source_group_keys: [sourceItem.group_key],
        current_preview_key: this.selectedKey,
        current_preview_load_rfq_unit_id: this.currentPreviewLoadRfqUnitId,
      },
      options,
      selectName: 'target_group_key',
      onSubmit: null,
    })
  }

  openMultiSelectionMergeModal(selectedGroupKeys) {
    const selectedItems = selectedGroupKeys.map((groupKey) => this.itemForGroupKey(groupKey)).filter(Boolean)
    if (!selectedItems.length) return

    const laneKeys = selectedItems.map((item) => this.laneKey(item))
    const uniqueLaneKeys = Array.from(new Set(laneKeys.filter((laneKey) => String(laneKey || '').length > 0)))
    const mixedRoutes = uniqueLaneKeys.length > 1 || laneKeys.some((laneKey) => String(laneKey || '').length === 0)

    this.renderModal({
      title: 'Merge Selected Trucks',
      introText: "Pick which checked draft truck to keep. All other checked draft trucks will be rebuilt onto that truck's equipment after you confirm.",
      action: this.mergeTrucksUrlValue,
      hiddenFields: {
        current_preview_key: this.selectedKey,
        current_preview_load_rfq_unit_id: this.currentPreviewLoadRfqUnitId,
      },
      options: [
        {
          label: 'Checked draft trucks',
          options: selectedItems.map((item) => ({
            value: item.group_key,
            label: this.mergeOptionLabel(item),
          })),
        },
      ],
      selectName: 'target_group_key',
      selectedValue: this.defaultBulkMergeTargetGroupKey(selectedItems),
      submitDisabled: mixedRoutes,
      inlineMessage: mixedRoutes ? 'Selected draft trucks must share the same lane to merge.' : null,
      onSubmit: (selectedValue, form) => this.decorateSelectedMergeForm(form, selectedValue, selectedItems),
    })
  }

  buildSingleMergeOptions(sourceItem) {
    const sourceLaneKey = this.laneKey(sourceItem)
    const sourceGroupKey = sourceItem.group_key

    const drafts = []
    const itemsIter = this.itemsByKey.values ? Array.from(this.itemsByKey.values()) : []
    itemsIter.forEach((item) => {
      if (item.source_type !== 'project_draft') return
      if (item.group_key === sourceGroupKey) return

      const metadata = item.metadata || {}
      const label = `${metadata.title || metadata.equipment_name || 'Draft truck'} · ${metadata.origin || '?'} → ${metadata.destination || '?'} · ${metadata.load_count || 0} pieces`
      drafts.push({
        value: item.group_key,
        label,
        sameRoute: this.laneKey(item) === sourceLaneKey,
      })
    })

    const sameRouteOptions = drafts.filter((option) => option.sameRoute)

    const groups = []
    if (sameRouteOptions.length) groups.push({ label: 'Same lane', options: sameRouteOptions })

    return groups
  }

  mergeOptionLabel(item) {
    const metadata = item.metadata || {}
    return `${metadata.title || metadata.equipment_name || 'Draft truck'} · ${metadata.origin || '?'} → ${metadata.destination || '?'} · ${metadata.load_count || 0} pieces`
  }

  defaultBulkMergeTargetGroupKey(selectedItems) {
    const previewGroupKey = this.selectedDraftGroupKey(this.selectedKey)
    if (previewGroupKey && selectedItems.some((item) => item.group_key === previewGroupKey)) {
      return previewGroupKey
    }

    return selectedItems[0] && selectedItems[0].group_key
  }

  laneKey(item) {
    if (item && Object.prototype.hasOwnProperty.call(item, 'lane_key')) {
      return item.lane_key
    }

    const metadata = item.metadata || {}
    return `${metadata.origin || ''}||${metadata.destination || ''}`
  }

  renderModal({
    title,
    introText,
    action,
    hiddenFields,
    options,
    selectName,
    onSubmit,
    selectedValue = null,
    submitDisabled = false,
    inlineMessage = null,
    selectLabel = 'Target',
  }) {
    if (!this.hasModalTarget) return

    this.setModalSelectVisible(true)
    this.setModalSelectLabel(selectLabel)
    this.modalTitleTarget.textContent = title
    this.modalIntroTarget.textContent = introText
    this.modalFormTarget.setAttribute('action', action)
    this.modalFormTarget.setAttribute('method', 'post')
    this.modalSubmitTarget.textContent = 'Confirm'
    this.clearModalError()

    // Rebuild form body: CSRF + hidden fields + select
    const form = this.modalFormTarget

    // Remove any stale dynamic inputs (class-marked so we don't clobber the static body/footer).
    form.querySelectorAll('[data-dynamic="true"]').forEach((node) => node.remove())

    // CSRF
    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = this.csrfToken()
    csrfInput.dataset.dynamic = 'true'
    form.appendChild(csrfInput)

    // expanded_keys echoed back
    const expandedInput = document.createElement('input')
    expandedInput.type = 'hidden'
    expandedInput.name = 'expanded_keys'
    expandedInput.value = this.element.dataset.expandedKeys || ''
    expandedInput.dataset.dynamic = 'true'
    form.appendChild(expandedInput)

    this.appendHiddenFields(form, hiddenFields)

    // Build <select> options
    const select = this.modalSelectTarget
    select.name = selectName
    select.replaceChildren()

    const hasOptions = options.some((group) => group.options.length)
    if (!hasOptions) {
      const opt = document.createElement('option')
      opt.value = ''
      opt.textContent = 'No eligible targets'
      opt.disabled = true
      opt.selected = true
      select.appendChild(opt)
    } else {
      options.forEach((group) => {
        if (!group.options.length) return
        const optgroup = document.createElement('optgroup')
        optgroup.label = group.label
        group.options.forEach((option) => {
          const opt = document.createElement('option')
          opt.value = option.value
          opt.textContent = option.label
          optgroup.appendChild(opt)
        })
        select.appendChild(optgroup)
      })

      if (selectedValue !== undefined && selectedValue !== null) {
        select.value = String(selectedValue)
      }
    }

    this.modalSubmitTarget.disabled = submitDisabled || !hasOptions
    if (inlineMessage) {
      this.renderModalError(inlineMessage, submitDisabled ? 'warning' : 'info')
    }

    // Submit hook: decode prefix for Move
    this.modalOnSubmit = onSubmit
    this.showModal()
  }

  renderConfirmModal({ title, introText, action, hiddenFields, submitText = 'Confirm' }) {
    if (!this.hasModalTarget) return

    this.setModalSelectVisible(false)
    this.modalTitleTarget.textContent = title
    this.modalIntroTarget.textContent = introText
    this.modalFormTarget.setAttribute('action', action)
    this.modalFormTarget.setAttribute('method', 'post')
    this.modalSubmitTarget.textContent = submitText
    this.modalSubmitTarget.disabled = false
    this.clearModalError()

    const form = this.modalFormTarget
    form.querySelectorAll('[data-dynamic="true"]').forEach((node) => node.remove())

    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = this.csrfToken()
    csrfInput.dataset.dynamic = 'true'
    form.appendChild(csrfInput)

    const expandedInput = document.createElement('input')
    expandedInput.type = 'hidden'
    expandedInput.name = 'expanded_keys'
    expandedInput.value = this.element.dataset.expandedKeys || ''
    expandedInput.dataset.dynamic = 'true'
    form.appendChild(expandedInput)

    this.modalSelectTarget.name = '_target'
    this.modalSelectTarget.replaceChildren()
    this.appendHiddenFields(form, hiddenFields)

    this.modalOnSubmit = null
    this.showModal()
  }

  setModalSelectLabel(text) {
    if (!this.hasModalSelectGroupTarget) return
    const label = this.modalSelectGroupTarget.querySelector('label')
    if (label) label.textContent = text
  }

  setModalSelectVisible(visible) {
    if (!this.hasModalSelectGroupTarget) return

    this.modalSelectGroupTarget.classList.toggle('hidden', !visible)
  }

  stableFilterForMove() {
    if (this.activeFilter && this.activeFilter.type !== 'keys') return this.activeFilter
    if (this.selectionParentFilter && this.selectionParentFilter.type !== 'keys') return this.selectionParentFilter
    return null
  }

  selectLoadForMove(key, rfqUnitId, { preserveWorkspace = false } = {}) {
    const numericRfqUnitId = Number(rfqUnitId)
    if (!Number.isFinite(numericRfqUnitId)) return

    // A piece was picked for a move — clear any lingering orientation nudge.
    this.clearFleetEmptyHint()

    this.clearMergeMode()
    this.clearSplitMode()
    const switchingSourcePiece = this.pieceSelectionActive() && String(this.activeMoveSourceKey) === String(key)
    this.preserveMoveWorkspace = preserveWorkspace || switchingSourcePiece
    const returnFilter = switchingSourcePiece ? this.moveReturnFilter : this.stableFilterForMove()
    // Destination selection replaces passive truck inspection. The source key
    // remains in activeMoveSourceKey; it must not become a second Back level.
    if (this.preserveMoveWorkspace) {
      // Do not clear the renderer's scene context here: that would briefly put
      // every truck back in its fleet-grid position before the new destination
      // response arrives. selectLoad below replaces the visual selection in
      // place while the controller selection state is reset directly.
      this.selectedKey = null
      this.currentPreviewLoadRfqUnitId = null
    } else {
      this.clearSelectedKey()
    }
    if (this.activeFilter && this.activeFilter.type === 'keys') {
      if (returnFilter) {
        this.setFilter(returnFilter)
      } else {
        this.clearFilter()
      }
    }
    this.selectionParentFilter = null
    this.moveReturnFilter = returnFilter
    this.currentPreviewLoadRfqUnitId = numericRfqUnitId
    this.activeMoveSourceKey = key
    this.selectedMoveTarget = null
    this.pendingMoveCompletion = null
    this.moveSceneSignature = null
    this.syncRowSelection()
    this.syncSelectionDataset()

    this.ensureOperationalPreview()
    if (!this.preserveMoveWorkspace) {
      this.setPreviewSceneContext([key], { minimalLabels: true })
    }
    if (this.fleetRenderer && typeof this.fleetRenderer.selectLoad === 'function') {
      this.fleetRenderer.selectLoad(key, rfqUnitId, { refit: !this.preserveMoveWorkspace })
    }

    this.fetchMoveTargetsForLoad(rfqUnitId)
  }

  async fetchMoveTargetsForLoad(rfqUnitId) {
    if (!this.hasMoveTargetsUrlValue) {
      this.renderMovePanelError('Move targets are unavailable.')
      return
    }

    this.abortMoveTargetsRequest()
    const cacheKey = String(rfqUnitId)
    const cachedPayload = this.moveTargetsCacheByRfqUnitId.get(cacheKey)
    if (cachedPayload) {
      this.applyMoveTargetsPayload(cachedPayload)
      return
    }

    const abortController = new AbortController()
    this.moveTargetsAbortController = abortController
    this.activeMovePayload = null
    this.moveSceneSignature = null
    this.moveTargetsByKey = new Map()
    if (this.fleetRenderer && typeof this.fleetRenderer.setMoveTargets === 'function') {
      // Keep the current two-truck workspace and camera steady while the next
      // piece's destination is being checked. The stale target highlight and
      // placement disappear, but the rigs themselves do not collapse inward.
      this.fleetRenderer.setMoveTargets([], { refit: false })
    }
    this.renderMovePanelLoading()

    try {
      const existingPayload = await this.fetchMoveTargetsPayload(rfqUnitId, 'existing', abortController)
      if (abortController.signal.aborted) return

      this.applyMoveTargetsPayload(existingPayload, { newTargetsLoading: !existingPayload.error })
      if (existingPayload.error) {
        this.moveTargetsCacheByRfqUnitId.set(cacheKey, existingPayload)
        return
      }

      try {
        const newTrailerPayload = await this.fetchMoveTargetsPayload(rfqUnitId, 'new_trailers', abortController)
        if (abortController.signal.aborted) return

        if (newTrailerPayload.error) {
          const mergedPayload = this.mergeMoveTargetsPayload(existingPayload, { new_trailer_options: [] })
          this.applyMoveTargetsPayload(mergedPayload, { newTargetsError: newTrailerPayload.error })
          return
        }

        const mergedPayload = this.mergeMoveTargetsPayload(existingPayload, newTrailerPayload)
        this.moveTargetsCacheByRfqUnitId.set(cacheKey, mergedPayload)
        this.applyMoveTargetsPayload(mergedPayload)
      } catch (error) {
        if (error && error.name === 'AbortError') return
        const message = error instanceof Error ? error.message : 'Add-truck options are unavailable.'
        const mergedPayload = this.mergeMoveTargetsPayload(existingPayload, { new_trailer_options: [] })
        this.applyMoveTargetsPayload(mergedPayload, { newTargetsError: message })
      }
    } catch (error) {
      if (error && error.name === 'AbortError') return
      this.renderMovePanelError(error instanceof Error ? error.message : 'Move targets are unavailable.')
    } finally {
      if (this.moveTargetsAbortController === abortController) {
        this.moveTargetsAbortController = null
      }
    }
  }

  async fetchMoveTargetsPayload(rfqUnitId, scope, abortController) {
    const url = new URL(this.moveTargetsUrlValue, window.location.origin)
    url.searchParams.set('rfq_unit_id', String(rfqUnitId))
    if (scope) url.searchParams.set('scope', scope)

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: abortController.signal,
    })
    const payload = await response.json()
    if (!response.ok) {
      throw new Error(payload && payload.error ? payload.error : 'Move targets are unavailable.')
    }

    return payload
  }

  mergeMoveTargetsPayload(basePayload, nextPayload) {
    const merged = {
      ...(basePayload || {}),
      ...(nextPayload || {}),
      source: (basePayload && basePayload.source) || (nextPayload && nextPayload.source) || null,
      load: (basePayload && basePayload.load) || (nextPayload && nextPayload.load) || null,
      existing_targets: Array.isArray(basePayload && basePayload.existing_targets) ? basePayload.existing_targets : [],
      new_trailer_options: Array.isArray(nextPayload && nextPayload.new_trailer_options) ? nextPayload.new_trailer_options : [],
    }

    if (!(basePayload && basePayload.error)) {
      delete merged.error
    }

    return merged
  }

  applyMoveTargetsPayload(payload, options = {}) {
    this.activeMovePayload = payload
    this.selectedMoveTarget = null
    const existingTargets = Array.isArray(payload && payload.existing_targets) ? payload.existing_targets : []
    const enabledTargets = this.rankedEnabledMoveTargets(existingTargets)
    this.moveTargetsByKey = new Map(
      existingTargets
        .filter((target) => target && target.key)
        .map((target) => [target.key, target])
    )
    const source = (payload && payload.source) || {}
    const sourceKey = source.key || this.activeMoveSourceKey
    // One 3D marker per truck: the best-ranked enabled target (deck move or
    // stack) claims the marker; the rest stay reachable as action-bar chips
    // and via clicks on the base piece itself.
    const sceneTargets = []
    const seenSceneKeys = new Set()
    enabledTargets.forEach((target) => {
      const sceneKey = this.targetSceneKey(target)
      if (!sceneKey || seenSceneKeys.has(sceneKey)) return
      seenSceneKeys.add(sceneKey)
      sceneTargets.push({
        ...target,
        key: sceneKey,
        highlight_label: this.targetSceneLabel(target),
      })
    })
    const contextKeys = [sourceKey, ...sceneTargets.map((target) => target.key)].filter(Boolean)
    const sceneSignature = JSON.stringify({
      sourceKey: sourceKey || null,
      targets: enabledTargets.map((target) => [
        target.key,
        target.kind || 'draft',
        target.stack_on_rfq_unit_id || null,
        target.status || 'clear',
        target.reason || '',
      ]),
    })

    // Existing destinations arrive before new-equipment alternatives. The
    // second response usually changes only the panel, so preserve any orbit or
    // pan the operator has already made unless the visible 3D choices changed.
    if (sceneSignature !== this.moveSceneSignature) {
      this.moveSceneSignature = sceneSignature
      this.ensureOperationalPreview()
      const canSetMoveTargets = this.fleetRenderer && typeof this.fleetRenderer.setMoveTargets === 'function'
      const preserveWorkspace = this.preserveMoveWorkspace && Boolean(sourceKey)
      this.setPreviewSceneContext(contextKeys, {
        compact: enabledTargets.length > 0 || preserveWorkspace,
        minimalLabels: true,
        refit: !canSetMoveTargets && !preserveWorkspace,
        anchorKey: preserveWorkspace ? sourceKey : null,
        preserveAnchor: preserveWorkspace,
        enterDistance_in: 72,
      })
      if (canSetMoveTargets) {
        this.fleetRenderer.setMoveTargets(sceneTargets, {
          focusKeys: [sourceKey].filter(Boolean),
          sourceLabel: sourceKey ? this.sourceSceneLabel(sourceKey, source) : null,
          refit: !preserveWorkspace,
        })
      }
    }
    this.defaultMovePreviewTarget = enabledTargets.find((target) => target && target.projection) || null
    this.restoreDefaultMoveTargetPreview()
    this.renderMovePanel(payload, options)
  }

  abortMoveTargetsRequest() {
    if (this.moveTargetsAbortController) {
      this.moveTargetsAbortController.abort()
      this.moveTargetsAbortController = null
    }
  }

  clearMoveMode({ preservePreviewLoad = false } = {}) {
    this.abortMoveTargetsRequest()
    this.activeMovePayload = null
    this.moveSceneSignature = null
    this.moveTargetsByKey = new Map()
    this.defaultMovePreviewTarget = null
    this.selectedMoveTarget = null
    this.pendingMoveCompletion = null
    this.preserveMoveWorkspace = false
    this.activeMoveSourceKey = null
    this.moveReturnFilter = null
    if (!preservePreviewLoad) {
      this.currentPreviewLoadRfqUnitId = null
    }
    if (this.fleetRenderer && typeof this.fleetRenderer.setMoveTargets === 'function') {
      this.fleetRenderer.setMoveTargets([])
    }
    this.clearMoveTargetPreview()
    if (this.fleetRenderer && typeof this.fleetRenderer.selectLoad === 'function') {
      this.fleetRenderer.selectLoad(null, null)
    }
    if (this.fleetRenderer && typeof this.fleetRenderer.selectItem === 'function') {
      this.fleetRenderer.selectItem(this.selectedKey || null)
    }
    this.clearPreviewSceneContext()
    this.syncSelectionDataset()
    this.hideMovePanel()
    this.syncPreviewContext()
  }

  /* ----- Action bar (shared anatomy for move / merge / split modes) ----- */

  resetActionBar({ badge = null, title = '', vitals = '', status = '', previewStatus } = {}) {
    if (!this.hasMovePanelTarget) return

    delete this.movePanelTarget.dataset.state

    if (this.hasActionBarBadgeTarget) {
      this.actionBarBadgeTarget.textContent = badge || ''
      this.actionBarBadgeTarget.classList.toggle('hidden', !badge)
    }
    this.movePanelTitleTarget.textContent = title
    this.movePanelTitleTarget.classList.toggle('hidden', !title)
    if (this.hasActionBarVitalsTarget) {
      this.actionBarVitalsTarget.textContent = vitals || ''
      this.actionBarVitalsTarget.classList.toggle('hidden', !vitals)
    }
    this.movePanelStatusTarget.textContent = status
    this.movePanelStatusTarget.classList.toggle('hidden', !status)
    const header = this.movePanelTitleTarget.closest('.project-load-plan__action-bar-header')
    if (header) header.classList.toggle('hidden', !(badge || title || vitals || status))
    this.clearActionBarTargets()
    this.newTruckOptionsTarget.classList.remove('project-load-plan__destination-cards')
    this.newTruckOptionsTarget.replaceChildren()
    this.movePanelTarget.classList.remove('hidden')
    this.syncPreviewContext({ status: previewStatus === undefined ? status : previewStatus })
  }

  showActionBarStatus(message) {
    if (!this.hasMovePanelStatusTarget) return

    this.movePanelStatusTarget.textContent = message || ''
    this.movePanelStatusTarget.classList.toggle('hidden', !message)
    const header = this.movePanelStatusTarget.closest('.project-load-plan__action-bar-header')
    if (header) header.classList.toggle('hidden', !message)
  }

  clearActionBarTargets() {
    if (!this.hasActionBarTargetsTarget) return

    this.actionBarTargetsTarget.replaceChildren()
    this.actionBarTargetsTarget.classList.add('hidden')
  }

  rankedEnabledMoveTargets(targets) {
    return (Array.isArray(targets) ? targets : [])
      .map((target, index) => ({ target, index }))
      .filter(({ target }) => target && !target.disabled)
      .sort((leftEntry, rightEntry) => {
        const statusRank = (target) => target.status === 'clear' ? 0 : (target.status === 'permit' ? 1 : 2)
        return statusRank(leftEntry.target) - statusRank(rightEntry.target) || leftEntry.index - rightEntry.index
      })
      .map(({ target }) => target)
  }

  makeActionBarTargetButton(target, text, onSelect) {
    const chip = document.createElement('button')
    chip.type = 'button'
    chip.className = target.status === 'permit'
      ? 'project-load-plan__target-chip project-load-plan__target-chip--permit'
      : 'project-load-plan__target-chip'
    chip.textContent = text
    if (target.reason) chip.title = target.reason
    chip.addEventListener('mouseenter', () => {
      this.hoverFleetItem(this.targetSceneKey(target))
      this.previewMoveTarget(target)
    })
    chip.addEventListener('mouseleave', () => {
      this.hoverFleetItem(null)
      this.restoreDefaultMoveTargetPreview()
    })
    chip.addEventListener('focus', () => this.previewMoveTarget(target))
    chip.addEventListener('blur', () => this.restoreDefaultMoveTargetPreview())
    chip.addEventListener('click', () => {
      this.hoverFleetItem(null)
      onSelect(target)
    })
    return chip
  }

  // The scene carries alternate valid destinations. Keep the docked bar to one
  // explicit default; merge can expose additional destinations progressively.
  renderActionBarTargets(targets, onSelect, { verb = 'Move to', includeAlternates = true } = {}) {
    if (!this.hasActionBarTargetsTarget) return

    const container = this.actionBarTargetsTarget
    container.replaceChildren()
    const list = Array.isArray(targets) ? targets.filter(Boolean) : []
    const enabled = this.rankedEnabledMoveTargets(list)
    if (!enabled.length) {
      container.classList.add('hidden')
      return
    }

    const chipText = (target) => (
      target.kind === 'stack' || target.kind === 'unstack'
        ? this.stackTargetLabel(target)
        : `${verb} ${this.targetChoiceLabel(target)}`
    )
    const primary = enabled[0]
    container.appendChild(this.makeActionBarTargetButton(
      primary,
      chipText(primary),
      onSelect
    ))

    if (primary.status === 'permit') {
      const consequence = document.createElement('span')
      consequence.className = 'project-load-plan__decision-consequence project-load-plan__decision-consequence--permit'
      consequence.textContent = primary.reason ? `Permit required · ${primary.reason}` : 'Permit required'
      container.appendChild(consequence)
    }

    // Stack/unstack targets are distinct actions, not alternate destinations.
    // Keep unstack visible even when many bases are available, then expose any
    // bases beyond the compact row in an explicit keyboard-accessible list.
    const stackingActions = enabled.slice(1)
      .filter((target) => target.kind === 'stack' || target.kind === 'unstack')
    const unstackActions = stackingActions.filter((target) => target.kind === 'unstack')
    const stackActions = stackingActions.filter((target) => target.kind === 'stack')
    const inlineStackCount = Math.max(0, 3 - unstackActions.length)
    const inlineStackingActions = [...unstackActions, ...stackActions.slice(0, inlineStackCount)]
    inlineStackingActions.forEach((target) => {
      container.appendChild(this.makeActionBarTargetButton(target, chipText(target), onSelect))
    })

    const overflowStackActions = stackActions.slice(inlineStackCount)
    if (!includeAlternates && overflowStackActions.length) {
      const details = document.createElement('details')
      details.className = 'project-load-plan__destination-options'
      const summary = document.createElement('summary')
      summary.textContent = 'More stacking options'
      summary.setAttribute('aria-expanded', 'false')
      summary.addEventListener('click', (event) => {
        event.preventDefault()
        details.open = !details.open
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false')
      })
      details.appendChild(summary)
      overflowStackActions.forEach((target) => {
        details.appendChild(this.makeActionBarTargetButton(target, chipText(target), onSelect))
      })
      container.appendChild(details)
    }

    if (includeAlternates && enabled.length > 1) {
      const details = document.createElement('details')
      details.className = 'project-load-plan__destination-options'
      const summary = document.createElement('summary')
      summary.textContent = 'Choose another truck'
      details.appendChild(summary)
      enabled.slice(1).forEach((target) => {
        details.appendChild(this.makeActionBarTargetButton(
          target,
          chipText(target),
          onSelect
        ))
      })
      container.appendChild(details)
    }
    container.classList.remove('hidden')
  }

  hoverFleetItem(key) {
    if (this.fleetRenderer && typeof this.fleetRenderer.hoverItem === 'function') {
      this.fleetRenderer.hoverItem(key)
    }
  }

  previewMoveTarget(target) {
    if (!(target && this.targetSceneKey(target) && target.projection && this.fleetRenderer)) return
    if (typeof this.fleetRenderer.setMoveTargetPreview === 'function') {
      this.fleetRenderer.setMoveTargetPreview(this.targetSceneKey(target), target.projection)
    }
  }

  clearMoveTargetPreview() {
    if (this.fleetRenderer && typeof this.fleetRenderer.clearMoveTargetPreview === 'function') {
      this.fleetRenderer.clearMoveTargetPreview()
    }
  }

  restoreDefaultMoveTargetPreview() {
    if (this.defaultMovePreviewTarget) {
      this.previewMoveTarget(this.defaultMovePreviewTarget)
    } else {
      this.clearMoveTargetPreview()
    }
  }

  loadVitalsText(key, rfqUnitId) {
    const item = this.itemsByKey && this.itemsByKey.get(key)
    if (!item) return ''

    const rows = Array.isArray(item.load_rows) ? item.load_rows : []
    const source = rows.find((row) => String(row.rfq_unit_id) === String(rfqUnitId)) || item.load_visual
    if (!source) return ''

    const inches = (value) => {
      const numeric = Number(value)
      return Number.isFinite(numeric) && numeric > 0 ? format.FT.to.FT.IN.DEC(3)(numeric / 12) : null
    }
    const parts = []
    const dims = [
      inches(source.length_in) && `L ${inches(source.length_in)}`,
      inches(source.width_in) && `W ${inches(source.width_in)}`,
      inches(source.height_in) && `H ${inches(source.height_in)}`,
    ].filter(Boolean)
    if (dims.length) parts.push(dims.join(' · '))
    const weight = this.formatWeight(source.weight_lb)
    if (weight) parts.push(weight)
    return parts.join(' · ')
  }

  // Idle hint shown when a truck (not a piece) is selected: points the user at
  // the piece-move workflow. No badge — this is an idle state, not an active
  // mode. Replaced by real status the moment a piece is picked or a mode starts.
  renderTruckSelectedHint(key) {
    if (!key) return

    this.hideMovePanel()
    this.setPreviewSceneContext([key], { minimalLabels: true })
    this.fitPreviewView('selection')
    this.syncPreviewContext({ status: this.selectedTruckStatus(key) })
  }

  renderMovePanelLoading() {
    if (!this.hasMovePanelTarget) return

    this.resetActionBar({
      title: 'Checking where this piece can go…',
      vitals: this.loadVitalsText(this.activeMoveSourceKey, this.currentPreviewLoadRfqUnitId),
      previewStatus: '',
    })
    this.movePanelTarget.dataset.mode = 'move'
    this.revealMovePanel()
  }

  renderMovePanelError(message) {
    if (!this.hasMovePanelTarget) return

    this.resetActionBar({
      title: 'Move unavailable',
      status: message || 'Move targets are unavailable.',
      previewStatus: '',
    })
    this.movePanelTarget.dataset.mode = 'move'
    this.revealMovePanel()
  }

  renderMovePanelNonTarget(key = null) {
    if (!this.hasMovePanelTarget) return

    const payload = this.activeMovePayload || {}
    const existingTargets = (Array.isArray(payload.existing_targets) ? payload.existing_targets : [])
      .filter((target) => target && !target.disabled)
    const newTrailerOptions = (Array.isArray(payload.new_trailer_options) ? payload.new_trailer_options : [])
      .filter((target) => target && !target.disabled)

    const target = key && this.moveTargetsByKey ? this.moveTargetsByKey.get(key) : null
    let message
    if (target && target.reason) {
      message = `That truck is unavailable: ${target.reason}`
    } else if (key && String(key) === String(this.activeMoveSourceKey)) {
      // The source is already marked Current in the staged scene. Preserve the
      // useful destination explanation instead of replacing it with a fact the
      // operator can already see.
      return
    } else if (existingTargets.length) {
      message = 'That truck cannot take this piece. Choose a highlighted truck instead.'
    } else if (newTrailerOptions.length) {
      message = 'No other truck on this route can take this piece.'
    } else {
      message = 'That truck cannot take this piece.'
    }
    this.showActionBarStatus(message)
    this.movePanelTarget.classList.remove('hidden')
    this.syncPreviewContext({ status: '' })
    this.revealMovePanel()
  }

  renderMovePanel(payload, options = {}) {
    if (!this.hasMovePanelTarget) return

    const load = (payload && payload.load) || {}
    const source = (payload && payload.source) || {}
    // Unplanned (loose) pieces have no saved truck yet, so the whole bar is
    // framed as an assignment rather than a move (mirrors openMoveConfirmation).
    const assigningUnplanned = source.kind === 'unplanned'
    const newTargetsLoading = Boolean(options.newTargetsLoading)
    const newTargetsError = options.newTargetsError
    const rawExistingTargets = Array.isArray(payload && payload.existing_targets) ? payload.existing_targets : []
    const existingTargets = rawExistingTargets.filter((target) => target && !target.disabled)
    const existingTruckCount = new Set(
      existingTargets.map((target) => this.targetSceneKey(target)).filter(Boolean)
    ).size
    const newTrailerOptions = (Array.isArray(payload && payload.new_trailer_options) ? payload.new_trailer_options : [])
      .filter((target) => target && !target.disabled)

    let title = ''
    let status = ''
    let showUnplannedDeadEnd = false
    if (payload && payload.error) {
      title = assigningUnplanned ? 'Assignment unavailable' : 'Move unavailable'
      status = payload.error
    } else if (existingTargets.length) {
      title = 'Choose a highlighted truck'
      status = `${existingTruckCount} ${existingTruckCount === 1 ? 'truck can' : 'trucks can'} take this piece.`
    } else if (newTargetsLoading) {
      title = 'Checking equipment…'
    } else if (newTargetsError) {
      title = assigningUnplanned ? 'Assignment unavailable' : 'Move unavailable'
      status = newTargetsError
    } else if (newTrailerOptions.length) {
      title = assigningUnplanned
        ? 'No truck on this route can take this piece.'
        : 'No other truck on this route can take this piece.'
    } else if (assigningUnplanned) {
      title = 'Assignment unavailable'
      status = 'No truck can take this piece yet. Choose equipment for it in the Unassigned Pieces list, or use + New to create custom equipment.'
      showUnplannedDeadEnd = true
    } else {
      title = 'Move unavailable'
      status = 'No eligible truck is available for this piece.'
    }

    this.resetActionBar({
      title,
      vitals: this.loadVitalsText(this.activeMoveSourceKey, this.currentPreviewLoadRfqUnitId),
      status,
      previewStatus: '',
    })
    this.movePanelTarget.dataset.mode = assigningUnplanned ? 'assign' : 'move'
    const noExistingDestination = !existingTargets.length && newTrailerOptions.length > 0 && !newTargetsLoading && !newTargetsError
    if (noExistingDestination) this.movePanelTarget.dataset.state = 'no-target'
    if (showUnplannedDeadEnd) {
      this.appendUnplannedDeadEndAction(load.rfq_unit_id || source.rfq_unit_id || this.currentPreviewLoadRfqUnitId)
    }
    this.renderActionBarTargets(
      rawExistingTargets,
      (target) => this.selectMoveTarget(target),
      {
        verb: assigningUnplanned ? 'Assign to' : 'Move to',
        includeAlternates: false,
      }
    )
    if (noExistingDestination) {
      this.renderNoTargetChoices(newTrailerOptions[0], { allowRemove: !assigningUnplanned, source })
    } else if (!existingTargets.length) {
      this.renderNewTruckOptions(newTrailerOptions, {
        preferExisting: false,
        existingTargets: [],
        onExistingSelect: null,
        allowUnassign: !assigningUnplanned,
      })
    }
    this.revealMovePanel()
  }

  // Dead-end helper for a loose piece nothing can take: surfaces a "Show in
  // list" control that scrolls the matching Unassigned Pieces row into view and
  // flashes it, so the user can pick equipment there. Appended into the status
  // area (below the status line) so it does not disturb the target-chip row.
  appendUnplannedDeadEndAction(rfqUnitId) {
    if (!this.hasMovePanelStatusTarget || rfqUnitId == null) return

    const row = this.unplannedRowFor(rfqUnitId)
    if (!row) return

    const action = document.createElement('button')
    action.type = 'button'
    action.className = 'btn btn-link btn-xs project-load-plan__unplanned-jump'
    action.textContent = 'Show in list'
    action.addEventListener('click', () => this.flashUnplannedRow(rfqUnitId))
    this.movePanelStatusTarget.appendChild(document.createTextNode(' '))
    this.movePanelStatusTarget.appendChild(action)
  }

  // Scrolls a loose piece's row in the Unassigned Pieces table into view and
  // reuses the priced-row landing flash (is-flash-highlighted) so the jump is
  // easy to follow.
  flashUnplannedRow(rfqUnitId) {
    const row = this.unplannedRowFor(rfqUnitId)
    if (!(row && typeof row.scrollIntoView === 'function')) return

    row.scrollIntoView({ behavior: 'smooth', block: 'center' })
    row.classList.add('is-flash-highlighted')
    window.setTimeout(() => row.classList.remove('is-flash-highlighted'), 1600)
  }

  // The Unassigned Pieces table row for a loose piece. Scoped to that table so
  // it never resolves to a same-id .project-load-plan__load-row in the main
  // equipment table (both carry data-rfq-unit-id).
  unplannedRowFor(rfqUnitId) {
    const table = document.querySelector('[data-load-planning-unplanned-warning="true"]')
    if (!table) return null

    return table.querySelector(`tr[data-rfq-unit-id="${rfqUnitId}"]`)
  }

  revealMovePanel() {
    if (!this.hasMovePanelTarget) return
    if (this.movePanelTarget.classList.contains('hidden')) return

    // Completing a move can happen in the 3D scene by clicking a highlighted truck, so the
    // render and the panel need to be visible together. Anchor on the render host
    // and only scroll when it isn't already comfortably in view — that keeps 3D
    // clicks (render already on screen) from jumping, while pulling the viewport up
    // when the move was triggered from a table row far down the page. Run synchronously
    // (no rAF) so the scroll still happens when the tab is backgrounded.
    const anchor = this.hasFleetContainerTarget ? this.fleetContainerTarget : this.movePanelTarget
    const rect = anchor.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const comfortablyVisible = rect.top >= -40 && rect.top <= viewportHeight * 0.5
    if (comfortablyVisible) return

    anchor.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  renderNoTargetChoices(recommendedTarget, { allowRemove = true, source = {} } = {}) {
    if (!(this.hasNewTruckOptionsTarget && recommendedTarget)) return

    const container = this.newTruckOptionsTarget
    container.replaceChildren()
    container.classList.add('project-load-plan__destination-cards')

    const makeChoice = ({ className = '', title, detail, consequence, ariaLabel, onSelect }) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = `project-load-plan__destination-card ${className}`.trim()
      button.setAttribute('aria-label', ariaLabel || title)

      const titleNode = document.createElement('span')
      titleNode.className = 'project-load-plan__destination-card-title'
      titleNode.textContent = title
      button.appendChild(titleNode)

      const detailNode = document.createElement('span')
      detailNode.className = 'project-load-plan__destination-card-detail'
      detailNode.textContent = detail
      button.appendChild(detailNode)

      if (consequence) {
        const consequenceNode = document.createElement('span')
        consequenceNode.className = 'project-load-plan__destination-card-consequence'
        consequenceNode.textContent = consequence
        button.appendChild(consequenceNode)
      }

      button.addEventListener('click', onSelect)
      return button
    }

    const equipment = this.equipmentTypeLabel(recommendedTarget)
    const permitText = recommendedTarget.status === 'permit'
      ? (recommendedTarget.reason ? `Permit required · ${recommendedTarget.reason}` : 'Permit required')
      : null
    const recommendationReason = recommendedTarget.recommendation_reason || 'Recommended equipment for this piece'
    container.appendChild(makeChoice({
      className: 'project-load-plan__destination-card--recommended',
      title: `Add another ${equipment}`,
      detail: recommendationReason,
      consequence: permitText,
      ariaLabel: `Add another ${equipment} truck and move this piece to it`,
      onSelect: () => this.submitMoveChoice(recommendedTarget),
    }))

    if (allowRemove) {
      container.appendChild(makeChoice({
        title: 'Take it off this truck',
        detail: 'Keep it in the load plan and assign it later',
        consequence: source && source.source_will_be_deleted ? 'The empty truck will also be removed' : null,
        ariaLabel: 'Take this piece off its current truck and keep it in the load plan',
        onSelect: () => this.submitRemoveChoice(),
      }))
    }
  }

  moveChoiceRequest(target) {
    const payload = this.activeMovePayload
    if (!(payload && target && !target.disabled)) return null

    const source = payload.source || {}
    const load = payload.load || {}
    const rfqUnitId = load.rfq_unit_id || source.rfq_unit_id || this.currentPreviewLoadRfqUnitId
    if (!rfqUnitId) return null

    const assigningUnplanned = source.kind === 'unplanned'
    // The successful Turbo response is deliberately held until the operator
    // chooses All trucks. Ask the server for an overview response so applying
    // it at that point does not create another hidden selection/back level.
    const hiddenFields = { rfq_unit_id: rfqUnitId }
    if (target.kind === 'draft' || target.kind === 'stack' || target.kind === 'unstack') {
      hiddenFields.target_group_key = target.target_group_key
      if (target.kind === 'stack') hiddenFields.stack_on_rfq_unit_id = target.stack_on_rfq_unit_id
      // A projected draft-to-draft move stays in the contextual action bar so
      // the operator can review placement before returning to the fleet. Tell
      // the server not to append a delayed global success notice to the held
      // Turbo response.
      if (target.key && target.projection) hiddenFields.success_feedback_surface = 'action_bar'
    } else if (target.kind === 'new_trailer') {
      Object.assign(hiddenFields, this.newTrailerSelectionFields(target))
    } else {
      return null
    }

    return {
      action: assigningUnplanned ? this.assignUnplannedLoadUrlValue : this.moveLoadUrlValue,
      hiddenFields,
    }
  }

  submitMoveChoice(target) {
    const request = this.moveChoiceRequest(target)
    if (!request) return

    const loadLabel = this.loadDisplayLabel(this.activeMoveSourceKey, this.currentPreviewLoadRfqUnitId)
    const targetLabel = target.kind === 'draft'
      ? this.targetChoiceLabel(target)
      : (target.kind === 'stack' || target.kind === 'unstack' ? this.stackTargetLabel(target) : `another ${this.equipmentTypeLabel(target)}`)
    const pendingMessage = target.kind === 'stack'
      ? `Stacking ${loadLabel}…`
      : (target.kind === 'unstack' ? `Placing ${loadLabel} back on the deck…` : `Moving ${loadLabel} to ${targetLabel}…`)
    this.submitMoveDecision({
      ...request,
      pendingMessage,
      moveTarget: target,
      loadLabel,
      targetLabel,
    })
  }

  submitRemoveChoice() {
    const payload = this.activeMovePayload || {}
    const source = payload.source || {}
    const load = payload.load || {}
    const rfqUnitId = load.rfq_unit_id || source.rfq_unit_id || this.currentPreviewLoadRfqUnitId
    if (!rfqUnitId) return

    this.submitMoveDecision({
      action: this.removeLoadUrlValue,
      hiddenFields: {
        rfq_unit_id: rfqUnitId,
        current_preview_key: this.activeMoveSourceKey,
      },
    })
  }

  async submitMoveDecision({
    action,
    hiddenFields,
    pendingMessage = 'Updating the load plan…',
    moveTarget = null,
    loadLabel = null,
    targetLabel = null,
  }) {
    if (!action) return

    const buttons = this.hasMovePanelTarget
      ? Array.from(this.movePanelTarget.querySelectorAll(
        '.project-load-plan__destination-card, .project-load-plan__target-chip, .project-load-plan__confirm-move'
      ))
      : []
    buttons.forEach((button) => { button.disabled = true })
    if (this.hasMovePanelTarget) this.movePanelTarget.classList.add('is-submitting')
    this.showActionBarStatus(pendingMessage)

    const params = new URLSearchParams()
    params.set('authenticity_token', this.csrfToken())
    params.set('expanded_keys', this.element.dataset.expandedKeys || '')
    Object.entries(hiddenFields || {}).forEach(([name, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(name, String(value))
    })
    // Validation feedback for these contextual actions belongs beside the
    // decision that failed, not in a second transient global notice.
    params.set('feedback_surface', 'action_bar')

    try {
      const response = await fetch(action, {
        method: 'POST',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-CSRF-Token': this.csrfToken(),
        },
        body: params.toString(),
        credentials: 'same-origin',
      })
      const body = await response.text()

      if (!response.ok) {
        if (this.connected) {
          this.showActionBarStatus(
            this.localMutationErrorMessage(body) || 'The load plan could not be updated. Try again.'
          )
        }
        return
      }

      const canShowCompletedMove = Boolean(
        body.trim().length > 0 &&
        moveTarget &&
        ['draft', 'stack', 'unstack'].includes(moveTarget.kind) &&
        moveTarget.key &&
        moveTarget.projection
      )

      if (canShowCompletedMove) {
        const animated = await this.animateMoveToTargetWithTimeout(moveTarget)
        if (this.connected) {
          // If the transfer visual could not run at all, drop the stale blue
          // ghost so the committed move is not shown as still pending.
          if (!animated) this.clearMoveTargetPreview()
          this.holdCompletedMove({ body, target: moveTarget, loadLabel, targetLabel })
        }
      } else {
        window.projectLoadPlanningPendingFilterSignature = this.element.dataset.activeFilterSignature || null
        if (body.trim().length > 0) Turbo.renderStreamMessage(body)
      }
    } catch (_error) {
      if (this.connected) this.showActionBarStatus('The load plan could not be updated. Try again.')
    } finally {
      if (this.connected) {
        buttons.forEach((button) => { button.disabled = false })
        if (this.hasMovePanelTarget) this.movePanelTarget.classList.remove('is-submitting')
      }
    }
  }

  animateMoveToTargetWithTimeout(target) {
    const renderer = this.fleetRenderer
    if (!(renderer && typeof renderer.animateMoveToTarget === 'function')) {
      return Promise.resolve(false)
    }

    return new Promise((resolve) => {
      let settled = false
      let timeoutId = null
      const finish = (completed) => {
        if (settled) return

        settled = true
        if (timeoutId !== null) window.clearTimeout(timeoutId)
        resolve(Boolean(completed))
      }

      timeoutId = window.setTimeout(() => finish(false), MOVE_TRANSFER_ANIMATION_TIMEOUT_MS)
      Promise.resolve()
        .then(() => renderer.animateMoveToTarget(
          this.activeMoveSourceKey,
          this.currentPreviewLoadRfqUnitId,
          this.targetSceneKey(target),
          target.projection
        ))
        .then(finish)
        // The mutation has already committed. A visual-animation failure must
        // not strand the held response or misreport the successful move.
        .catch(() => finish(false))
    })
  }

  localMutationErrorMessage(body) {
    if (!(body && typeof DOMParser !== 'undefined')) return ''

    const document = new DOMParser().parseFromString(body, 'text/html')
    const stream = document.querySelector(
      'turbo-stream[target="project-load-plan-modal-error"]'
    )
    const template = stream && stream.querySelector('template')
    const content = template && template.content ? template.content : template
    return content ? content.textContent.replace(/\s+/g, ' ').trim() : ''
  }

  renderNewTruckOptions(options, {
    preferExisting = false,
    existingTargets = [],
    onExistingSelect = null,
    existingVerb = 'Move to',
    allowUnassign = false,
  } = {}) {
    if (!this.hasNewTruckOptionsTarget) return

    this.newTruckOptionsTarget.replaceChildren()
    const availableOptions = Array.isArray(options) ? options.filter(Boolean) : []
    const alternateExistingTargets = preferExisting && typeof onExistingSelect === 'function'
      ? this.rankedEnabledMoveTargets(existingTargets).slice(1)
      : []
    if (!availableOptions.length && !alternateExistingTargets.length && !allowUnassign) return

    const appendConsequence = (container, option) => {
      if (!(option && option.status === 'permit')) return

      const consequence = document.createElement('span')
      consequence.className = 'project-load-plan__decision-consequence project-load-plan__decision-consequence--permit'
      consequence.textContent = option.reason ? `Permit required · ${option.reason}` : 'Permit required'
      container.appendChild(consequence)
    }

    const appendPrimaryTruck = (container) => {
      const primary = availableOptions[0]
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'btn btn-primary btn-sm'
      button.textContent = `Create ${this.equipmentTypeLabel(primary)} truck`
      button.addEventListener('click', () => this.openMoveConfirmationForNewTruck(primary))

      container.appendChild(button)
      appendConsequence(container, primary)
    }

    const appendOtherEquipment = (container) => {
      if (availableOptions.length <= 1) return

      const picker = document.createElement('div')
      picker.className = 'project-load-plan__new-truck-picker'

      const select = document.createElement('select')
      select.className = 'form-control input-sm'
      select.setAttribute('aria-label', 'Different equipment')
      availableOptions.slice(1).forEach((option) => {
        const opt = document.createElement('option')
        opt.value = this.newTrailerSelectionValue(option)
        opt.textContent = option.label || `Trailer #${option.tractor_trailer_id}`
        select.appendChild(opt)
      })

      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'btn btn-default btn-sm'
      button.textContent = 'Use this equipment'
      button.addEventListener('click', () => {
        const selected = availableOptions.find((option) => this.newTrailerSelectionValue(option) === String(select.value))
        if (selected) this.openMoveConfirmationForNewTruck(selected)
      })

      picker.appendChild(select)
      picker.appendChild(button)
      container.appendChild(picker)
    }

    const appendExistingAlternatives = (container) => {
      alternateExistingTargets.forEach((target) => {
        container.appendChild(this.makeActionBarTargetButton(
          target,
          `${existingVerb} ${this.targetChoiceLabel(target)}`,
          onExistingSelect
        ))
      })
    }

    const appendUnassign = (container) => {
      if (!allowUnassign) return

      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'btn btn-link btn-xs project-load-plan__return-unassigned'
      button.textContent = 'Leave unassigned'
      button.addEventListener('click', () => this.openRemoveConfirmation())
      container.appendChild(button)
    }

    if (preferExisting) {
      const hasAnotherTruck = alternateExistingTargets.length > 0 || availableOptions.length > 0
      const details = document.createElement('details')
      details.className = 'project-load-plan__other-options'
      const summary = document.createElement('summary')
      summary.textContent = hasAnotherTruck ? 'Choose another truck' : "Don't place this piece"
      details.appendChild(summary)
      appendExistingAlternatives(details)
      if (availableOptions.length) {
        appendPrimaryTruck(details)
        appendOtherEquipment(details)
      }
      appendUnassign(details)
      this.newTruckOptionsTarget.appendChild(details)
      return
    }

    if (availableOptions.length) appendPrimaryTruck(this.newTruckOptionsTarget)
    if (availableOptions.length > 1 || allowUnassign) {
      const details = document.createElement('details')
      details.className = 'project-load-plan__other-options'
      const summary = document.createElement('summary')
      summary.textContent = availableOptions.length > 1 ? 'Choose different equipment' : "Don't place this piece"
      details.appendChild(summary)
      appendOtherEquipment(details)
      appendUnassign(details)
      this.newTruckOptionsTarget.appendChild(details)
    }
  }

  openRemoveConfirmation() {
    const payload = this.activeMovePayload
    const source = (payload && payload.source) || {}
    const load = (payload && payload.load) || {}
    const rfqUnitId = load.rfq_unit_id || source.rfq_unit_id || this.currentPreviewLoadRfqUnitId
    if (!rfqUnitId) return

    const consequences = ['The piece will return to the unassigned list.']
    if (source.source_will_be_deleted) consequences.push('The empty source truck will be removed.')
    this.renderConfirmModal({
      title: 'Leave this piece unassigned?',
      introText: consequences.join(' '),
      action: this.removeLoadUrlValue,
      hiddenFields: {
        rfq_unit_id: rfqUnitId,
      },
      submitText: 'Leave unassigned',
    })
  }

  hideMovePanel() {
    if (!this.hasMovePanelTarget) return

    this.movePanelTarget.classList.add('hidden')
    delete this.movePanelTarget.dataset.mode
    delete this.movePanelTarget.dataset.state
    this.movePanelTarget.classList.remove('is-submitting')
    this.newTruckOptionsTarget.classList.remove('project-load-plan__destination-cards')
    this.newTruckOptionsTarget.replaceChildren()
    this.clearActionBarTargets()
    if (this.hasActionBarBadgeTarget) {
      this.actionBarBadgeTarget.classList.add('hidden')
    }
    if (this.hasActionBarVitalsTarget) {
      this.actionBarVitalsTarget.textContent = ''
    }
    this.syncPreviewContext()
  }

  openMoveConfirmationForExistingTarget(key) {
    if (this.moveCompletionPending()) return false
    const target = this.moveTargetsByKey && this.moveTargetsByKey.get(key)
    if (!(target && !target.disabled)) return false

    this.selectMoveTarget(target)
    return true
  }

  selectMoveTarget(target) {
    const payload = this.activeMovePayload
    const supportedKinds = ['draft', 'stack', 'unstack']
    if (!(payload && target && supportedKinds.includes(target.kind) && target.key && !target.disabled)) return

    // A clear destination moves on the first click — the ghost placement and
    // the target's "After move" vitals were already on screen while choosing.
    // Only a permit consequence earns an explicit confirmation stop.
    if (target.status !== 'permit') {
      this.submitMoveChoice(target)
      return
    }

    const source = payload.source || {}
    const assigningUnplanned = source.kind === 'unplanned'
    const loadLabel = this.loadDisplayLabel(this.activeMoveSourceKey, this.currentPreviewLoadRfqUnitId)
    const targetLabel = target.kind === 'stack' || target.kind === 'unstack' ? this.stackTargetLabel(target) : this.targetChoiceLabel(target)
    const sourceKey = source.key || this.activeMoveSourceKey
    const sceneTarget = { ...target, key: this.targetSceneKey(target), highlight_label: this.targetSceneLabel(target) }
    this.selectedMoveTarget = target
    this.defaultMovePreviewTarget = target.projection ? target : null

    this.setPreviewSceneContext([sourceKey, this.targetSceneKey(target)].filter(Boolean), {
      compact: true,
      minimalLabels: true,
      refit: false,
    })
    if (this.fleetRenderer && typeof this.fleetRenderer.setMoveTargets === 'function') {
      this.fleetRenderer.setMoveTargets([sceneTarget], {
        focusKeys: [sourceKey].filter(Boolean),
        sourceLabel: sourceKey ? this.sourceSceneLabel(sourceKey, source) : null,
      })
    }
    this.restoreDefaultMoveTargetPreview()

    const projected = target.truck && target.truck.projected
    const status = [
      projected ? `After move: ${this.truckVitalsText(projected)}` : null,
      target.status === 'permit'
        ? (target.reason ? `Permit required · ${target.reason}` : 'Permit required')
        : null,
    ].filter(Boolean).join(' · ')
    const confirmTitle = target.kind === 'stack'
      ? `${targetLabel}?`
      : (target.kind === 'unstack'
        ? `Put ${loadLabel} back on the deck?`
        : `${assigningUnplanned ? 'Assign' : 'Move'} ${loadLabel} to ${targetLabel}?`)
    this.resetActionBar({
      title: confirmTitle,
      vitals: this.loadVitalsText(this.activeMoveSourceKey, this.currentPreviewLoadRfqUnitId),
      status,
      previewStatus: '',
    })
    this.movePanelTarget.dataset.mode = assigningUnplanned ? 'assign' : 'move'
    this.movePanelTarget.dataset.state = 'confirm-target'

    const confirm = document.createElement('button')
    confirm.type = 'button'
    confirm.className = 'btn btn-primary btn-sm project-load-plan__confirm-move'
    confirm.textContent = target.kind === 'stack'
      ? 'Stack piece'
      : (target.kind === 'unstack' ? 'Put back on deck' : (assigningUnplanned ? 'Assign piece' : 'Move piece'))
    confirm.addEventListener('click', () => this.submitMoveChoice(target))
    this.actionBarTargetsTarget.replaceChildren(confirm)
    this.actionBarTargetsTarget.classList.remove('hidden')
    this.revealMovePanel()
    this.syncPreviewContext({ status: '' })
  }

  holdCompletedMove({ body, target, loadLabel, targetLabel }) {
    const resolvedLoadLabel = loadLabel || this.loadDisplayLabel(
      this.activeMoveSourceKey,
      this.currentPreviewLoadRfqUnitId
    )
    const resolvedTargetLabel = targetLabel || this.targetChoiceLabel(target)
    this.pendingMoveCompletion = {
      body,
      loadLabel: resolvedLoadLabel,
      targetLabel: resolvedTargetLabel,
      sourceKey: this.activeMoveSourceKey,
      targetKey: this.targetSceneKey(target),
      movedRfqUnitId: this.currentPreviewLoadRfqUnitId,
    }
    this.selectedMoveTarget = null
    this.moveTargetsByKey = new Map()
    this.defaultMovePreviewTarget = null
    if (this.fleetRenderer && typeof this.fleetRenderer.setMoveTargets === 'function') {
      this.fleetRenderer.setMoveTargets([], { refit: false, preservePlacement: true })
    }

    const completionTitle = target && target.kind === 'stack'
      ? resolvedTargetLabel.replace(/^Stack on /, 'Stacked on ')
      : (target && target.kind === 'unstack' ? 'Placed back on the deck' : `Moved to ${resolvedTargetLabel}`)
    const completionStatus = target && target.kind === 'stack'
      ? 'Review the stack. Verify securement and the base piece\'s structural capacity before dispatch, then press Done.'
      : 'Review the placement. Click the moved piece to move it back, another piece to keep unloading, or press Done.'
    this.resetActionBar({
      title: completionTitle,
      vitals: resolvedLoadLabel,
      status: completionStatus,
      previewStatus: '',
    })
    this.movePanelTarget.dataset.mode = 'move'
    this.movePanelTarget.dataset.state = 'move-complete'

    // Explicit exit beside the completion message: applies the held response
    // and returns to the full fleet, so the operator never has to hunt for
    // the All trucks control in the viewer corner.
    const done = document.createElement('button')
    done.type = 'button'
    done.className = 'btn btn-primary btn-sm project-load-plan__confirm-move'
    done.textContent = 'Done'
    done.addEventListener('click', () => this.previewBack())
    this.actionBarTargetsTarget.replaceChildren(done)
    this.actionBarTargetsTarget.classList.remove('hidden')

    this.revealMovePanel()
    this.syncPreviewContext({ status: '' })
  }

  flushPendingMoveCompletion() {
    const completion = this.pendingMoveCompletion
    if (!completion) return

    this.pendingMoveCompletion = null
    window.projectLoadPlanningPendingFilterSignature = null
    if (completion.body && completion.body.trim().length > 0) {
      Turbo.renderStreamMessage(completion.body)
    }
  }

  // The landed piece is drawn by the transfer layer and has no pick proxy, so
  // a scene click on it resolves to its truck. While a completed move is held,
  // route a click on the destination truck to the just-moved piece — the
  // one-click "move it back" path.
  continueMoveFromCompletedTarget(key) {
    const completion = this.pendingMoveCompletion
    if (!(completion && key && String(key) === String(completion.targetKey))) return false

    return this.continueMoveFromCompletedSource(key, completion.movedRfqUnitId)
  }

  // Clicking any piece while a completed move is held continues the workflow
  // without a detour through All trucks: another piece keeps unloading, and
  // the just-moved piece lifts again from its new truck. The held Turbo
  // response is applied first, then the piece is re-selected on the freshly
  // rendered workspace.
  continueMoveFromCompletedSource(key, rfqUnitId) {
    const completion = this.pendingMoveCompletion
    if (!(completion && key && rfqUnitId)) return false

    const viewState = this.fleetRenderer && typeof this.fleetRenderer.captureViewState === 'function'
      ? this.fleetRenderer.captureViewState()
      : null
    window.projectLoadPlanningPendingMoveWorkspace = {
      selectedRfqUnitId: String(rfqUnitId),
      movedRfqUnitId: String(completion.movedRfqUnitId),
      viewState,
    }
    this.pendingMoveCompletion = null
    window.projectLoadPlanningPendingFilterSignature = null
    window.projectLoadPlanningPendingMoveRfqUnitId = String(rfqUnitId)
    if (completion.body && completion.body.trim().length > 0) {
      Turbo.renderStreamMessage(completion.body)
    }
    return true
  }

  openMoveConfirmationForNewTruck(target) {
    if (!target) return

    this.openMoveConfirmation(target)
  }

  openMoveConfirmation(target) {
    const payload = this.activeMovePayload
    if (!(payload && target && !target.disabled)) return

    const source = payload.source || {}
    const load = payload.load || {}
    const rfqUnitId = load.rfq_unit_id || source.rfq_unit_id || this.currentPreviewLoadRfqUnitId
    if (!rfqUnitId) return

    const assigningUnplanned = source.kind === 'unplanned'
    const action = assigningUnplanned ? this.assignUnplannedLoadUrlValue : this.moveLoadUrlValue
    const hiddenFields = {
      rfq_unit_id: rfqUnitId,
    }

    if (target.kind === 'draft') {
      hiddenFields.target_group_key = target.target_group_key
    } else if (target.kind === 'new_trailer') {
      Object.assign(hiddenFields, this.newTrailerSelectionFields(target))
    } else {
      return
    }

    const createsTruck = target.kind === 'new_trailer'
    const actionLabel = assigningUnplanned ? 'Assign to' : 'Move to'
    const title = createsTruck
      ? `Create ${this.equipmentTypeLabel(target)} truck?`
      : `${actionLabel} ${this.targetChoiceLabel(target)}?`
    this.renderConfirmModal({
      title,
      introText: this.moveConfirmationText(payload, target),
      action,
      hiddenFields,
      submitText: createsTruck ? 'Create truck' : (assigningUnplanned ? 'Assign piece' : 'Move piece'),
    })
  }

  newTrailerSelectionValue(target) {
    return String(target && (target.equipment_candidate_key || target.tractor_trailer_id) || '')
  }

  newTrailerSelectionFields(target) {
    if (target && target.equipment_candidate_key) {
      return { equipment_candidate_key: target.equipment_candidate_key }
    }

    return { tractor_trailer_id: target && target.tractor_trailer_id }
  }

  moveConfirmationText(payload, target) {
    const source = (payload && payload.source) || {}
    const consequences = []
    if (target.status === 'permit') {
      consequences.push(target.reason ? `Permit required: ${target.reason}.` : 'Permit required.')
    }
    if (source.source_will_be_deleted) {
      consequences.push('The empty source truck will be removed.')
    }
    if (!consequences.length) {
      consequences.push(target.kind === 'new_trailer'
        ? 'This adds a new truck to the draft plan.'
        : 'This updates the draft plan.')
    }
    return consequences.join(' ')
  }

  decorateSelectedMergeForm(form, targetGroupKey, selectedItems) {
    if (!targetGroupKey) return false

    const sourceGroupKeys = selectedItems
      .map((item) => item.group_key)
      .filter((groupKey) => String(groupKey) !== String(targetGroupKey))

    if (!sourceGroupKeys.length) return false

    this.replaceHiddenInputs(form, 'source_group_keys[]', sourceGroupKeys)
    return true
  }

  handleMutationFormSubmit(event) {
    const form = event.target
    if (!(form && form.tagName === 'FORM')) return

    // Inject up-to-date expanded_keys + preview state into every mutation form.
    if (form === this.modalFormTarget) {
      event.preventDefault()

      // Modal form: let the modal's onSubmit decorator normalise the <select>
      // value (e.g. the merge modal's target/source mapping).
      if (typeof this.modalOnSubmit === 'function') {
        const ok = this.modalOnSubmit(this.modalSelectTarget.value, form)
        if (ok === false) {
          return
        }
      }
      // expanded_keys already added in renderModal, but refresh in case changed since.
      this.upsertHiddenInput(form, 'expanded_keys', this.element.dataset.expandedKeys || '')
      this.submitModalForm(form)
      return
    }

    // For inline button_to forms inside the panel, ensure preview state + expanded_keys are current.
    if (this.hasMutationFormTarget && this.mutationFormTargets.includes(form)) {
      this.upsertHiddenInput(form, 'expanded_keys', this.element.dataset.expandedKeys || '')
      this.upsertHiddenInput(form, 'current_preview_key', this.selectedKey)
      this.upsertHiddenInput(form, 'current_preview_load_rfq_unit_id', this.currentPreviewLoadRfqUnitId)

      switch (form.dataset.projectLoadPlanningBulkAction) {
        case 'quote-selected': {
          const groupKeys = this.selectedGroupKeysInDomOrder()
          if (!groupKeys.length) {
            event.preventDefault()
            return
          }

          this.replaceHiddenInputs(form, 'group_keys[]', groupKeys)
          this.removeHiddenInputs(form, 'group_key')

          if (
            groupKeys.length > BULK_QUOTE_CHARGE_CONFIRM_THRESHOLD &&
            this.approvedQuoteSelectionSignature !== this.selectedSelectionSignature()
          ) {
            event.preventDefault()
            this.openQuoteConfirmModal(groupKeys.length, form)
            return
          }
          break
        }
        case 'split': {
          const groupKey = this.splitEligibleGroupKey()
          if (!groupKey) {
            event.preventDefault()
            return
          }
          this.replaceHiddenInputs(form, 'group_key', [groupKey])
          this.removeHiddenInputs(form, 'group_keys[]')
          break
        }
        case 'delete-selected': {
          const groupKeys = this.selectedGroupKeysInDomOrder()
          if (!groupKeys.length) {
            event.preventDefault()
            return
          }
          this.replaceHiddenInputs(form, 'group_keys[]', groupKeys)
          this.removeHiddenInputs(form, 'group_key')
          break
        }
        default:
          break
      }
    }
  }

  upsertHiddenInput(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`)
    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      form.appendChild(input)
    }
    input.value = value || ''
  }

  appendHiddenFields(form, hiddenFields) {
    Object.entries(hiddenFields || {}).forEach(([name, value]) => {
      if (value === undefined || value === null || value === '') return

      const values = Array.isArray(value) ? value : [value]
      const inputName = Array.isArray(value) && !name.endsWith('[]') ? `${name}[]` : name
      values.forEach((entry) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = inputName
        input.value = String(entry)
        input.dataset.dynamic = 'true'
        form.appendChild(input)
      })
    })
  }

  replaceHiddenInputs(form, name, values) {
    this.removeHiddenInputs(form, name)
    const list = Array.isArray(values) ? values : [values]
    list.forEach((value) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = String(value)
      input.dataset.dynamic = 'true'
      form.appendChild(input)
    })
  }

  removeHiddenInputs(form, name) {
    form.querySelectorAll(`input[name="${name}"]`).forEach((input) => input.remove())
  }

  closeModal() {
    this.clearModalError()
    this.hideModal()
  }

  async submitModalForm(form) {
    if (!form.action) return

    this.modalSubmitTarget.disabled = true
    this.clearModalError()

    try {
      const params = new URLSearchParams(new FormData(form))
      params.set('feedback_surface', 'modal')
      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-CSRF-Token': this.csrfToken(),
        },
        body: params.toString(),
        credentials: 'same-origin',
      })
      const body = await response.text()

      if (response.ok) {
        window.projectLoadPlanningPendingFilterSignature = this.element.dataset.activeFilterSignature || null
        this.closeModal()
      }

      if (body.trim().length > 0) {
        Turbo.renderStreamMessage(body)
      }
    } catch (_error) {
      this.renderModalError('Request failed. Please try again.')
    } finally {
      if (this.hasModalSubmitTarget) {
        this.modalSubmitTarget.disabled = false
      }
    }
  }

  showModal() {
    if (!this.hasModalTarget) return
    this.showBootstrapModal(this.modalTarget)
  }

  hideModal() {
    if (!this.hasModalTarget) return
    this.showBootstrapModal(this.modalTarget, false)
  }

  showBootstrapModal(element, show = true) {
    if (!element) return

    if (window.jQuery && window.jQuery.fn && window.jQuery.fn.modal) {
      window.jQuery(element).modal(show ? 'show' : 'hide')
      return
    }

    element.style.display = show ? 'block' : 'none'
    element.classList.toggle('in', show)
  }

  clearModalError() {
    if (!this.hasModalErrorTarget) return

    this.modalErrorTarget.replaceChildren()
  }

  renderModalError(message, variant = 'danger') {
    if (!this.hasModalErrorTarget) return

    const alert = document.createElement('div')
    alert.className = `alert alert-${variant}`
    alert.setAttribute('role', 'alert')
    alert.textContent = message
    this.modalErrorTarget.replaceChildren(alert)
  }

  /* ----- Fleet layout (unchanged logic from prior implementation) ----- */

  prepareFleetItems(fleetItems, trailer3d) {
    const preparedItems = fleetItems
      .map((item, index) => {
        if (!(item && item.render_scene)) return null

        const renderOverrides = this.hydrateRenderOverridesWithLoadRows(item.render_overrides, item.load_rows)
        const renderState = trailer3d.buildTrailerRenderState(item.render_scene, {
          stretchMode: 'closed',
          index,
          renderOverrides,
        })
        const footprint = trailer3d.getTrailerRenderFootprint(renderState)
        const lengthIn = Number(footprint.maxX_in) - Number(footprint.minX_in)
        if (!(Number.isFinite(lengthIn) && lengthIn > 0 && Number.isFinite(Number(footprint.width_in)) && Number(footprint.width_in) > 0)) {
          return null
        }

        return this.groupFleetRenderItem({
          ...item,
          renderState,
          footprint,
          label: item.metadata && item.metadata.title ? item.metadata.title : item.key,
        })
      })
      .filter(Boolean)

    return this.centerFleetLayout(this.layoutFleetItems(preparedItems))
  }

  // Consolidated quote rows remain one logical/interactive fleet item, but
  // their quantity is rendered as a compact formation of real truck models.
  // Keeping the formation inside one item means every copy still selects the
  // same quote row and summary/table filters continue to operate on the quote
  // group rather than on synthetic browser-only trucks.
  groupFleetRenderItem(item) {
    const metadata = (item && item.metadata) || {}
    const rawQuantity = Number(metadata.truck_quantity)
    const truckQuantity = Number.isSafeInteger(rawQuantity) && rawQuantity > 1 ? rawQuantity : 1
    if (truckQuantity === 1) return item

    const unitFootprint = item.footprint || {}
    const minXIn = Number(unitFootprint.minX_in)
    const maxXIn = Number(unitFootprint.maxX_in)
    const unitWidthIn = Number(unitFootprint.width_in)
    const unitLengthIn = maxXIn - minXIn
    if (!(Number.isFinite(unitLengthIn) && unitLengthIn > 0 && Number.isFinite(unitWidthIn) && unitWidthIn > 0)) {
      return item
    }

    const columnPitchIn = unitLengthIn + TRUCK_GROUP_GAP_IN
    const rowPitchIn = unitWidthIn + TRUCK_GROUP_GAP_IN
    // Choose enough columns to keep large quantities roughly square while
    // naturally leaving ordinary 2-3 truck quotes in an easy-to-read convoy.
    const columnCount = Math.max(
      1,
      Math.min(truckQuantity, Math.ceil(Math.sqrt((truckQuantity * rowPitchIn) / columnPitchIn)))
    )
    const rowCount = Math.ceil(truckQuantity / columnCount)
    const groupWidthIn = (rowCount * unitWidthIn) + ((rowCount - 1) * TRUCK_GROUP_GAP_IN)
    const renderInstances = Array.from({ length: truckQuantity }, (_value, index) => {
      const columnIndex = index % columnCount
      const rowIndex = Math.floor(index / columnCount)
      return {
        index,
        localOffset_in: {
          x_in: (columnIndex * columnPitchIn) - minXIn,
          z_in: (rowIndex * rowPitchIn) + (unitWidthIn / 2) - (groupWidthIn / 2),
        },
      }
    })

    const populatedColumnCount = Math.min(columnCount, truckQuantity)
    return {
      ...item,
      unitFootprint,
      renderInstances,
      footprint: {
        minX_in: 0,
        maxX_in: (populatedColumnCount * unitLengthIn) + ((populatedColumnCount - 1) * TRUCK_GROUP_GAP_IN),
        width_in: groupWidthIn,
      },
    }
  }

  prepareUnplannedItems(unplannedItems, fleetItems = []) {
    const preparedItems = unplannedItems
      .map((item) => this.prepareUnplannedItem(item))
      .filter(Boolean)

    return this.layoutUnplannedItems(preparedItems, fleetItems)
  }

  prepareUnplannedItem(item) {
    const loadVisual = item && item.load_visual
    if (!loadVisual) return null
    const renderLoadVisual = this.renderLoadVisualForUnplannedPiece(loadVisual)

    const x0In = Number(renderLoadVisual.x0_in)
    const x1In = Number(renderLoadVisual.x1_in)
    const widthIn = Number(renderLoadVisual.width_in)
    if (!(Number.isFinite(x0In) && Number.isFinite(x1In) && x1In > x0In && Number.isFinite(widthIn) && widthIn > 0)) {
      return null
    }

    return {
      ...item,
      render_load_visual: renderLoadVisual,
      footprint: {
        minX_in: x0In,
        maxX_in: x1In,
        width_in: widthIn,
      },
      label: item.description || (item.metadata && item.metadata.title) || item.key,
      worldOffset_in: { x_in: 0, z_in: 0 },
    }
  }

  renderLoadVisualForUnplannedPiece(loadVisual) {
    const visualKind = loadVisual && (loadVisual.visual_kind || loadVisual.visualKind)
    if (visualKind !== 'missing_dimensions') return loadVisual

    const displayDimension = value => {
      const number = Number(value)
      return Number.isFinite(number) && number > 0 ? number : MISSING_DIMENSION_PLACEHOLDER_IN
    }
    const lengthIn = displayDimension(loadVisual.length_in)
    const widthIn = displayDimension(loadVisual.width_in)
    const heightIn = displayDimension(loadVisual.height_in)

    // These bounds exist only on the browser-side render copy. The original
    // load_visual keeps its nil dimensions so labels and editing never expose
    // the placeholder size as imported cargo data.
    return {
      ...loadVisual,
      x0_in: 0,
      x1_in: lengthIn,
      bottom_in: 0,
      top_in: heightIn,
      length_in: lengthIn,
      width_in: widthIn,
      height_in: heightIn,
      center_z_in: 0,
      placeholder_dimension_in: MISSING_DIMENSION_PLACEHOLDER_IN,
    }
  }

  layoutUnplannedItems(items, fleetItems = []) {
    if (!items.length) return []

    const fleetBounds = this.layoutBounds(fleetItems)
    const longestItemIn = items.reduce((maxLength, item) => Math.max(maxLength, this.footprintLengthIn(item)), 0)
    const totalFootprintArea = items.reduce(
      (sum, item) => sum + (this.footprintLengthIn(item) * Number(item.footprint.width_in)),
      0
    )
    const targetRowWidthIn = Math.max(longestItemIn, Math.sqrt(totalFootprintArea) * ROW_WIDTH_MULTIPLIER)
    const startXIn = fleetBounds ? fleetBounds.minX_in : 0
    let cursorXIn = startXIn
    let rowStartZIn = fleetBounds ? fleetBounds.maxZ_in + BUCKET_GAP_IN : 0
    let rowDepthIn = 0

    return items.map((item) => {
      const itemLengthIn = this.footprintLengthIn(item)
      const itemWidthIn = Number(item.footprint.width_in)

      if (cursorXIn > startXIn && (cursorXIn + itemLengthIn) > (startXIn + targetRowWidthIn)) {
        cursorXIn = startXIn
        rowStartZIn += rowDepthIn + FLEET_GAP_IN
        rowDepthIn = 0
      }

      const laidOutItem = {
        ...item,
        worldOffset_in: {
          x_in: cursorXIn - Number(item.footprint.minX_in),
          z_in: rowStartZIn + (itemWidthIn / 2),
        },
      }

      cursorXIn += itemLengthIn + FLEET_GAP_IN
      rowDepthIn = Math.max(rowDepthIn, itemWidthIn)
      return laidOutItem
    })
  }

  // Lays the matching loose pieces out as front-to-back bands, one per reason
  // (Heavy / Wide / Tall / Long), each with its own floating label. Within a
  // band pieces flow into rows exactly like the base grid.
  layoutUnplannedClusters(items, fleetItems = []) {
    const fleetBounds = this.layoutBounds(fleetItems)
    const longestItemIn = items.reduce((maxLength, item) => Math.max(maxLength, this.footprintLengthIn(item)), 0)
    const totalFootprintArea = items.reduce(
      (sum, item) => sum + (this.footprintLengthIn(item) * Number(item.footprint.width_in)),
      0
    )
    const targetRowWidthIn = Math.max(longestItemIn, Math.sqrt(totalFootprintArea) * ROW_WIDTH_MULTIPLIER)
    const startXIn = fleetBounds ? fleetBounds.minX_in : 0

    const positioned = []
    const labels = []
    let bandStartZIn = fleetBounds ? fleetBounds.maxZ_in + BUCKET_GAP_IN : 0

    this.groupItemsByPrimaryReason(items).forEach((group) => {
      let cursorXIn = startXIn
      let rowStartZIn = bandStartZIn
      let rowDepthIn = 0

      group.items.forEach((item) => {
        const itemLengthIn = this.footprintLengthIn(item)
        const itemWidthIn = Number(item.footprint.width_in)

        if (cursorXIn > startXIn && (cursorXIn + itemLengthIn) > (startXIn + targetRowWidthIn)) {
          cursorXIn = startXIn
          rowStartZIn += rowDepthIn + FLEET_GAP_IN
          rowDepthIn = 0
        }

        positioned.push({
          ...item,
          worldOffset_in: {
            x_in: cursorXIn - Number(item.footprint.minX_in),
            z_in: rowStartZIn + (itemWidthIn / 2),
          },
        })

        cursorXIn += itemLengthIn + FLEET_GAP_IN
        rowDepthIn = Math.max(rowDepthIn, itemWidthIn)
      })

      if (group.label) {
        labels.push({
          text: group.label,
          x_in: startXIn,
          z_in: bandStartZIn - CLUSTER_LABEL_GAP_IN,
        })
      }
      bandStartZIn = rowStartZIn + rowDepthIn + CLUSTER_GAP_IN
    })

    return { items: positioned, labels }
  }

  layoutFleetClassificationClusters(items) {
    if (!items.length) return { items: [], labels: [] }

    const longestItemIn = items.reduce((maxLength, item) => Math.max(maxLength, this.footprintLengthIn(item)), 0)
    const totalFootprintArea = items.reduce(
      (sum, item) => sum + (this.footprintLengthIn(item) * Number(item.footprint.width_in)),
      0
    )
    const targetRowWidthIn = Math.max(longestItemIn, Math.sqrt(totalFootprintArea) * ROW_WIDTH_MULTIPLIER)
    const startXIn = 0

    const positioned = []
    const labels = []
    let bandStartZIn = CLUSTER_LABEL_GAP_IN

    this.groupFleetItemsByClassification(items).forEach((group) => {
      let cursorXIn = startXIn
      let rowStartZIn = bandStartZIn
      let rowDepthIn = 0

      group.items.forEach((item) => {
        const itemLengthIn = this.footprintLengthIn(item)
        const itemWidthIn = Number(item.footprint.width_in)

        if (cursorXIn > startXIn && (cursorXIn + itemLengthIn) > (startXIn + targetRowWidthIn)) {
          cursorXIn = startXIn
          rowStartZIn += rowDepthIn + FLEET_GAP_IN
          rowDepthIn = 0
        }

        positioned.push({
          ...item,
          worldOffset_in: {
            x_in: cursorXIn - Number(item.footprint.minX_in),
            z_in: rowStartZIn + (itemWidthIn / 2),
          },
        })

        cursorXIn += itemLengthIn + FLEET_GAP_IN
        rowDepthIn = Math.max(rowDepthIn, itemWidthIn)
      })

      if (group.label) {
        labels.push({
          text: group.label,
          x_in: startXIn,
          z_in: bandStartZIn - CLUSTER_LABEL_GAP_IN,
        })
      }
      bandStartZIn = rowStartZIn + rowDepthIn + CLUSTER_GAP_IN
    })

    return this.centerLabeledLayout(positioned, labels)
  }

  groupFleetItemsByClassification(items) {
    const buckets = new Map()
    const labels = new Map()
    items.forEach((item) => {
      const metadata = item.metadata || {}
      const rawKey = metadata.load_classification
      const key = rawKey && (LOAD_CLASSIFICATION_LABELS[rawKey] || metadata.load_classification_label) ? rawKey : 'other'
      if (!buckets.has(key)) {
        buckets.set(key, [])
        labels.set(key, metadata.load_classification_label || LOAD_CLASSIFICATION_LABELS[key] || 'Other')
      }
      buckets.get(key).push(item)
    })

    const ordered = LOAD_CLASSIFICATION_ORDER
      .filter((classification) => buckets.has(classification))
      .map((classification) => ({
        classification,
        label: labels.get(classification),
        items: buckets.get(classification),
      }))

    buckets.forEach((groupItems, classification) => {
      if (LOAD_CLASSIFICATION_ORDER.includes(classification)) return

      ordered.push({
        classification,
        label: labels.get(classification),
        items: groupItems,
      })
    })

    return ordered
  }

  centerLabeledLayout(items, labels) {
    const bounds = this.layoutBounds(items)
    if (!bounds) return { items, labels }

    const centerXIn = (bounds.minX_in + bounds.maxX_in) / 2
    const centerZIn = (bounds.minZ_in + bounds.maxZ_in) / 2

    return {
      items: items.map((item) => ({
        ...item,
        worldOffset_in: {
          x_in: Number(item.worldOffset_in.x_in) - centerXIn,
          z_in: Number(item.worldOffset_in.z_in) - centerZIn,
        },
      })),
      labels: labels.map((label) => ({
        ...label,
        x_in: Number(label.x_in) - centerXIn,
        z_in: Number(label.z_in) - centerZIn,
      })),
    }
  }

  // Buckets pieces by their server-stamped primary reason, in display order.
  // Pieces with no oversized dimension collapse into a single unlabeled tail
  // band so a "Potential Normal Pieces" selection still pulls them aside.
  groupItemsByPrimaryReason(items) {
    const buckets = new Map()
    items.forEach((item) => {
      const reason = LOAD_REASON_LABELS[item.load_primary_reason] ? item.load_primary_reason : 'other'
      if (!buckets.has(reason)) buckets.set(reason, [])
      buckets.get(reason).push(item)
    })

    const ordered = LOAD_REASON_ORDER
      .filter((reason) => buckets.has(reason))
      .map((reason) => ({ reason, label: LOAD_REASON_LABELS[reason], items: buckets.get(reason) }))
    if (buckets.has('other')) {
      ordered.push({ reason: 'other', label: null, items: buckets.get('other') })
    }
    return ordered
  }

  layoutFleetItems(items) {
    if (!items.length) return []

    const longestItemIn = items.reduce((maxLength, item) => Math.max(maxLength, this.footprintLengthIn(item)), 0)
    const totalFootprintArea = items.reduce(
      (sum, item) => sum + (this.footprintLengthIn(item) * Number(item.footprint.width_in)),
      0
    )
    const targetRowWidthIn = Math.max(longestItemIn, Math.sqrt(totalFootprintArea) * ROW_WIDTH_MULTIPLIER)
    let cursorXIn = 0
    let rowStartZIn = 0
    let rowDepthIn = 0
    let activeBucketKey = this.bucketKeyFor(items[0])

    return items.map((item, index) => {
      const itemLengthIn = this.footprintLengthIn(item)
      const itemWidthIn = Number(item.footprint.width_in)
      const itemBucketKey = this.bucketKeyFor(item)

      if (index > 0 && itemBucketKey !== activeBucketKey) {
        cursorXIn = 0
        rowStartZIn += rowDepthIn + FLEET_GAP_IN + BUCKET_GAP_IN
        rowDepthIn = 0
        activeBucketKey = itemBucketKey
      }

      if (cursorXIn > 0 && (cursorXIn + itemLengthIn) > targetRowWidthIn) {
        cursorXIn = 0
        rowStartZIn += rowDepthIn + FLEET_GAP_IN
        rowDepthIn = 0
      }

      const laidOutItem = {
        ...item,
        worldOffset_in: {
          x_in: cursorXIn - Number(item.footprint.minX_in),
          z_in: rowStartZIn + (itemWidthIn / 2),
        },
      }

      cursorXIn += itemLengthIn + FLEET_GAP_IN
      rowDepthIn = Math.max(rowDepthIn, itemWidthIn)
      return laidOutItem
    })
  }

  centerFleetLayout(items) {
    if (!items.length) return []

    let minXIn = Infinity
    let maxXIn = -Infinity
    let minZIn = Infinity
    let maxZIn = -Infinity

    items.forEach((item) => {
      const offset = item.worldOffset_in || { x_in: 0, z_in: 0 }
      minXIn = Math.min(minXIn, Number(item.footprint.minX_in) + Number(offset.x_in))
      maxXIn = Math.max(maxXIn, Number(item.footprint.maxX_in) + Number(offset.x_in))
      minZIn = Math.min(minZIn, Number(offset.z_in) - (Number(item.footprint.width_in) / 2))
      maxZIn = Math.max(maxZIn, Number(offset.z_in) + (Number(item.footprint.width_in) / 2))
    })

    const centerXIn = (minXIn + maxXIn) / 2
    const centerZIn = (minZIn + maxZIn) / 2

    return items.map((item) => ({
      ...item,
      worldOffset_in: {
        x_in: Number(item.worldOffset_in.x_in) - centerXIn,
        z_in: Number(item.worldOffset_in.z_in) - centerZIn,
      },
    }))
  }

  layoutBounds(items) {
    if (!items.length) return null

    let minXIn = Infinity
    let maxXIn = -Infinity
    let minZIn = Infinity
    let maxZIn = -Infinity

    items.forEach((item) => {
      const footprint = item.footprint || {}
      const offset = item.worldOffset_in || { x_in: 0, z_in: 0 }
      const widthIn = Number(footprint.width_in)
      minXIn = Math.min(minXIn, Number(footprint.minX_in) + Number(offset.x_in || 0))
      maxXIn = Math.max(maxXIn, Number(footprint.maxX_in) + Number(offset.x_in || 0))
      minZIn = Math.min(minZIn, Number(offset.z_in || 0) - (widthIn / 2))
      maxZIn = Math.max(maxZIn, Number(offset.z_in || 0) + (widthIn / 2))
    })

    if (![minXIn, maxXIn, minZIn, maxZIn].every(Number.isFinite)) return null

    return {
      minX_in: minXIn,
      maxX_in: maxXIn,
      minZ_in: minZIn,
      maxZ_in: maxZIn,
    }
  }

  /* ----- small helpers ----- */

  footprintLengthIn(item) {
    return Number(item.footprint.maxX_in) - Number(item.footprint.minX_in)
  }

  bucketKeyFor(item) {
    return item && item.metadata && item.metadata.bucket_key ? item.metadata.bucket_key : ''
  }

  sanitizeRenderOverrides(renderOverrides) {
    if (!(renderOverrides && typeof renderOverrides === 'object')) return null
    return JSON.parse(JSON.stringify(renderOverrides))
  }

  hydrateRenderOverridesWithLoadRows(renderOverrides, loadRows) {
    const sanitized = this.sanitizeRenderOverrides(renderOverrides)
    const previews = sanitized && Array.isArray(sanitized.load_previews) ? sanitized.load_previews : []
    const rows = Array.isArray(loadRows) ? loadRows : []
    if (!(sanitized && previews.length && rows.length)) return sanitized

    sanitized.load_previews = previews.map((preview, index) => {
      if (!(preview && typeof preview === 'object')) return preview

      const row = this.loadRowForPreview(preview, rows, index, previews.length)
      if (!row) return preview

      const hydratedPreview = { ...preview }
      if (!this.presentValue(hydratedPreview.rfq_unit_id) && this.presentValue(row.rfq_unit_id)) {
        hydratedPreview.rfq_unit_id = row.rfq_unit_id
      }
      if (!this.presentValue(hydratedPreview.description) && this.presentValue(row.description)) {
        hydratedPreview.description = row.description
      }
      if (!this.presentValue(hydratedPreview.weight_lb) && this.presentValue(row.weight_lb)) {
        hydratedPreview.weight_lb = row.weight_lb
      }
      return hydratedPreview
    })
    sanitized.load_preview = sanitized.load_previews[0] || sanitized.load_preview
    return sanitized
  }

  loadRowForPreview(preview, rows, index, previewCount) {
    const indexedRow = rows[index]
    if (rows.length === previewCount && indexedRow && this.presentValue(indexedRow.rfq_unit_id)) {
      return indexedRow
    }

    const matches = rows.filter((row) => this.loadRowGeometryMatchesPreview(row, preview))
    return matches.length === 1 ? matches[0] : null
  }

  loadRowGeometryMatchesPreview(row, preview) {
    if (!(row && typeof row === 'object' && preview && typeof preview === 'object')) return false

    const previewX0In = Number(preview.x0_in)
    const previewX1In = Number(preview.x1_in)
    const rowX0In = Number(row.position_from_front_in)
    const rowLengthIn = Number(row.length_in)
    if (![previewX0In, previewX1In, rowX0In, rowLengthIn].every(Number.isFinite)) return false

    const samePosition =
      this.nearlyEqual(previewX0In, rowX0In) &&
      this.nearlyEqual(previewX1In - previewX0In, rowLengthIn)
    const sameSurface =
      !this.presentValue(preview.surface_id) ||
      !this.presentValue(row.surface_id) ||
      String(preview.surface_id) === String(row.surface_id)

    return samePosition && sameSurface
  }

  presentValue(value) {
    return value !== undefined && value !== null && String(value).length > 0
  }

  nearlyEqual(left, right, tolerance = 0.01) {
    return Math.abs(Number(left) - Number(right)) <= tolerance
  }

  /* ----- Summary panel + table filtering ----- */

  handleSummaryClick(event) {
    const bucketRow = event.target.closest('[data-behavior~="project-summary-bucket"]')
    if (bucketRow) {
      this.prepareForSummaryFilter()
      this.toggleFilter(this.filterForBucketRow(bucketRow))
      return
    }

    const classRow = event.target.closest('[data-behavior~="project-summary-class"]')
    if (classRow) {
      this.prepareForSummaryFilter()
      this.toggleFilter(this.filterForClassRow(classRow))
      return
    }

    const unassignedRow = event.target.closest('[data-behavior~="project-summary-unassigned"]')
    if (unassignedRow) {
      this.prepareForSummaryFilter()
      // Only scroll when the click turns the filter ON — the toggle-off click
      // should not yank the viewport down to the warning.
      if (this.toggleFilter({ type: 'unassigned', label: 'Unassigned pieces' })) {
        this.scrollUnplannedWarningIntoView()
      }
    }
  }

  prepareForSummaryFilter() {
    if (this.pieceSelectionActive()) this.clearMoveMode()
    if (this.mergeModeActive()) this.clearMergeMode()
    if (this.splitModeActive()) this.clearSplitMode()
    if (this.selectedKey) this.clearSelectedKey()
    this.selectionParentFilter = null
    this.hideMovePanel()
  }

  handleSummaryKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return
    if (!(event.target instanceof Element)) return
    if (!event.target.closest('[data-behavior~="project-summary-bucket"], [data-behavior~="project-summary-class"], [data-behavior~="project-summary-unassigned"]')) return

    event.preventDefault()
    this.handleSummaryClick(event)
  }

  filterForBucketRow(row) {
    const bucketKeys = (row.dataset.bucketKeys || '').split(',').map((key) => key.trim()).filter(Boolean)
    return {
      type: 'bucket',
      bucketKeys,
      signature: `bucket:${bucketKeys.join(',')}`,
      label: row.dataset.bucketLabel || 'Equipment',
    }
  }

  // A workspace refresh replaces this controller's element, so the jQuery
  // layer stashes the active filter signature before requesting it and the
  // freshly-mounted controller re-applies the filter here. Stale signatures
  // (e.g. the filtered truck no longer exists) are dropped silently.
  restorePendingFilter() {
    const signature = window.projectLoadPlanningPendingFilterSignature
    window.projectLoadPlanningPendingFilterSignature = null
    if (!signature) return

    const filter = this.filterForSignature(signature)
    if (filter) {
      this.setFilter(filter)
    }
  }

  pendingRowSelectionIdentity(key, excludedRfqUnitId) {
    const item = this.itemsByKey && this.itemsByKey.get(key)
    const excludedId = excludedRfqUnitId == null ? null : String(excludedRfqUnitId)
    const anchorLoad = Array.isArray(item && item.load_rows)
      ? item.load_rows.find((load) => {
        const rfqUnitId = load && load.rfq_unit_id
        return rfqUnitId != null && (excludedId == null || String(rfqUnitId) !== excludedId)
      })
      : null

    return {
      key: String(key),
      anchorRfqUnitId: anchorLoad ? String(anchorLoad.rfq_unit_id) : null,
    }
  }

  // A move rebuilds both affected groups under fresh keys. Preserve one
  // remaining piece from the clicked truck so the freshly-mounted controller
  // can resolve its replacement group after the held Turbo response lands.
  // Unaffected and empty trucks still resolve directly by their original key.
  restorePendingRowSelection() {
    const pending = window.projectLoadPlanningPendingRowSelection
    window.projectLoadPlanningPendingRowSelection = null
    if (!(pending && this.itemsByKey)) return

    let key = pending.key && this.itemsByKey.has(pending.key) ? pending.key : null
    if (!key && pending.anchorRfqUnitId) {
      const entry = Array.from(this.itemsByKey.entries()).find(([, item]) => (
        item && item.source_type === 'project_draft' &&
        Array.isArray(item.load_rows) && item.load_rows.some((load) => (
          String(load && load.rfq_unit_id) === String(pending.anchorRfqUnitId)
        ))
      ))
      key = entry && entry[0]
    }
    if (!key) return

    this.applySelectedKey(key)
  }

  restorePendingMoveSelection() {
    const rfqUnitId = window.projectLoadPlanningPendingMoveRfqUnitId
    const workspace = window.projectLoadPlanningPendingMoveWorkspace
    window.projectLoadPlanningPendingMoveRfqUnitId = null
    window.projectLoadPlanningPendingMoveWorkspace = null
    if (!rfqUnitId || !this.itemsByKey) return

    const entry = Array.from(this.itemsByKey.entries()).find(([, item]) => (
      Array.isArray(item && item.load_rows) && item.load_rows.some((load) => (
        String(load && load.rfq_unit_id) === String(rfqUnitId)
      ))
    ))
    if (!entry) return

    const sourceKey = entry[0]
    const previousDestinationEntry = workspace && workspace.movedRfqUnitId
      ? Array.from(this.itemsByKey.entries()).find(([key, item]) => (
        String(key) !== String(sourceKey) &&
        Array.isArray(item && item.load_rows) &&
        item.load_rows.some((load) => (
          String(load && load.rfq_unit_id) === String(workspace.movedRfqUnitId)
        ))
      ))
      : null
    const contextKeys = [sourceKey, previousDestinationEntry && previousDestinationEntry[0]].filter(Boolean)
    if (workspace && this.fleetRenderer) {
      this.setPreviewSceneContext(contextKeys, {
        compact: true,
        minimalLabels: true,
        animate: false,
        refit: false,
      })
      if (workspace.viewState && typeof this.fleetRenderer.restoreViewState === 'function') {
        this.fleetRenderer.restoreViewState(workspace.viewState)
      }
    }
    this.selectLoadForMove(sourceKey, rfqUnitId, { preserveWorkspace: Boolean(workspace) })
  }

  filterForSignature(signature) {
    if (signature === 'unassigned') {
      return { type: 'unassigned', label: 'Unassigned pieces' }
    }

    if (signature.startsWith('keys:')) {
      const key = signature.slice('keys:'.length)
      return this.itemsByKey && this.itemsByKey.has(key) ? this.filterForItemKey(key) : null
    }

    const summaryRows = document.querySelectorAll(
      '[data-behavior~="project-summary-bucket"], [data-behavior~="project-summary-class"]'
    )
    for (const row of summaryRows) {
      const filter = row.matches('[data-behavior~="project-summary-bucket"]')
        ? this.filterForBucketRow(row)
        : this.filterForClassRow(row)
      if (filter.signature === signature) return filter
    }
    return null
  }

  filterForClassRow(row) {
    const classKey = row.dataset.loadClass || ''
    const labelElement = row.querySelector('.project-load-plan__equipment-label')
    return {
      type: 'class',
      classKey,
      signature: `class:${classKey}`,
      label: labelElement ? labelElement.textContent.trim() : classKey,
    }
  }

  filterForItemKey(key) {
    const item = this.itemsByKey && this.itemsByKey.get(key)
    const metadata = (item && item.metadata) || {}
    return {
      type: 'keys',
      keys: [key],
      signature: `keys:${key}`,
      label: metadata.title || metadata.equipment_name || (item && item.description) || key,
    }
  }

  // Returns true when the filter was activated, false when it toggled off.
  toggleFilter(filter) {
    const signature = filter && (filter.signature || filter.type)
    const activeSignature = this.activeFilter && (this.activeFilter.signature || this.activeFilter.type)
    if (signature && signature === activeSignature) {
      this.clearFilter()
      return false
    }

    this.setFilter(filter)
    return true
  }

  setFilter(filter) {
    this.activeFilter = filter || null
    this.applyFilter()
  }

  clearFilter() {
    this.activeFilter = null
    this.applyFilter()
  }

  applyFilter() {
    const filter = this.activeFilter
    // Mirror the active filter onto the workbench root so the legacy jQuery
    // layer can stash it across workspace refreshes (see restorePendingFilter).
    if (filter && (filter.signature || filter.type)) {
      this.element.dataset.activeFilterSignature = filter.signature || filter.type
    } else {
      delete this.element.dataset.activeFilterSignature
    }
    const highlightKeys = filter ? this.fleetKeysForFilter(filter) : []
    const renderPlan = this.syncPreviewRenderPlan(filter)
    if (this.fleetRenderer && typeof this.fleetRenderer.highlightItems === 'function') {
      // In grouped preview views the matching items are already pulled into
      // labeled bands, so the outline overlay would just be visual noise.
      this.fleetRenderer.highlightItems(renderPlan.suppressHighlight ? [] : highlightKeys)
    }
    this.applyEquipmentTableFilter(filter, highlightKeys)
    this.applyLoadsTableFilter(filter)
    // Selections must follow visibility: prune draft-truck selections that the
    // filter just hid, and let the legacy jQuery layer do the same for the
    // RFQ pieces table.
    this.syncBulkSelection()
    this.notifyLoadsTableVisibilityChanged()
    this.syncFilterBar(filter)
    this.syncSummaryActiveState(filter)
    this.syncPreviewContext()
    this.fitPreviewView('auto')
  }

  // Builds the 3D scene for the active summary/table filter. Equipment summary
  // rows filter the preview down to matching trucks and group them by planned
  // classification; piece-class rows keep the full fleet visible and pull the
  // matching loose pieces into reason bands.
  syncPreviewRenderPlan(filter) {
    const fallback = { suppressHighlight: false, filtered: false }
    if (!(this.fleetRenderer && typeof this.fleetRenderer.render === 'function')) return fallback

    const plan = this.previewRenderPlan(filter)
    if (plan.signature !== this.currentPreviewRenderSignature) {
      this.currentPreviewRenderSignature = plan.signature
      // A full re-render wipes the renderer's selected-load and move-target
      // state, and the armed load may not even survive into the new layout — so
      // cancel any in-progress mode first, keeping the action bar and the 3D
      // scene in sync.
      if (this.pieceSelectionActive()) this.clearMoveMode()
      if (this.mergeModeActive()) this.clearMergeMode()
      if (this.splitModeActive()) this.clearSplitMode()
      const renderedKeys = new Set([...plan.items, ...plan.unplannedItems].map((item) => item.key))
      const rendererSelectedKey = this.selectedKey && renderedKeys.has(this.selectedKey)
        ? this.selectedKey
        : null
      this.fleetRenderer.render({
        items: plan.items,
        unplannedItems: plan.unplannedItems,
        selectedKey: rendererSelectedKey,
        autoSelect: false,
        showScaleFigure: true,
        clusterLabels: plan.labels,
      })
    }
    return plan
  }

  previewRenderPlan(filter) {
    const fleetItems = this.fleetPreparedItems || []
    const unplannedItems = this.baseUnplannedItems || []
    const basePlan = {
      signature: 'base',
      items: fleetItems,
      unplannedItems,
      labels: [],
      filtered: false,
      suppressHighlight: false,
    }

    if (filter && filter.type === 'bucket') {
      const buckets = new Set(filter.bucketKeys || [])
      const matching = fleetItems.filter((item) => item.metadata && buckets.has(item.metadata.bucket_key))
      if (matching.length) {
        const layout = this.layoutFleetClassificationClusters(matching)
        return {
          signature: `fleet-bucket:${Array.from(buckets).sort().join(',')}`,
          items: layout.items,
          unplannedItems: [],
          labels: layout.labels,
          filtered: true,
          suppressHighlight: true,
        }
      }
    }

    if (filter && filter.type === 'class') {
      const classByRfqId = this.loadClassByRfqId()
      const matching = unplannedItems.filter((item) => classByRfqId.get(String(item.rfq_id)) === filter.classKey)
      if (matching.length) {
        const layout = this.layoutUnplannedClusters(matching, fleetItems)
        return {
          signature: `piece-class:${filter.classKey}`,
          items: fleetItems,
          unplannedItems: layout.items,
          labels: layout.labels,
          filtered: true,
          suppressHighlight: true,
        }
      }
    }

    return basePlan
  }

  notifyLoadsTableVisibilityChanged() {
    document.dispatchEvent(new CustomEvent('project-rfqs:visibility-changed'))
  }

  fleetKeysForFilter(filter) {
    const items = this.itemsByKey ? Array.from(this.itemsByKey.values()) : []

    switch (filter.type) {
      case 'bucket': {
        const buckets = new Set(filter.bucketKeys || [])
        return items
          .filter((item) => item.metadata && buckets.has(item.metadata.bucket_key))
          .map((item) => item.key)
      }
      case 'class': {
        const classByRfqId = this.loadClassByRfqId()
        return items
          .filter((item) => item.source_type === 'unplanned_load' && classByRfqId.get(String(item.rfq_id)) === filter.classKey)
          .map((item) => item.key)
      }
      case 'unassigned':
        return items.filter((item) => item.source_type === 'unplanned_load').map((item) => item.key)
      case 'keys':
        return (filter.keys || []).filter((key) => this.itemsByKey && this.itemsByKey.has(key))
      default:
        return []
    }
  }

  applyEquipmentTableFilter(filter, highlightKeys) {
    if (!this.hasRowGroupTarget) return

    // Bucket membership comes from the server-stamped data-bucket-key, not
    // from client-prepared items, so trucks the 3D renderer could not prepare
    // still stay visible in their bucket's filtered view.
    const bucketKeys = filter && filter.type === 'bucket' ? new Set(filter.bucketKeys || []) : null
    const bucketScoped = Boolean(bucketKeys && bucketKeys.size)
    const keyScoped = Boolean(
      filter &&
        filter.type === 'keys' &&
        highlightKeys.some((key) => this.fleetItemsByKey && this.fleetItemsByKey.has(key))
    )
    const visibleKeys = new Set(highlightKeys)

    this.rowGroupTargets.forEach((group) => {
      let hidden = false
      if (bucketScoped) {
        hidden = !bucketKeys.has(group.dataset.bucketKey)
      } else if (keyScoped) {
        hidden = !visibleKeys.has(group.dataset.key)
      }
      group.classList.toggle('is-filtered-out', hidden)
    })
  }

  applyLoadsTableFilter(filter) {
    const rows = document.querySelectorAll('tr[data-rfq-row="true"]')
    if (!rows.length) return

    if (filter && filter.type === 'class') {
      rows.forEach((row) => {
        row.classList.toggle('is-filtered-out', row.dataset.loadClass !== filter.classKey)
      })
      this.hideOrphanedRfqDetailRows()
      return
    }

    let visibleRfqIds = null
    if (filter && filter.type === 'unassigned') {
      // Server-rendered truth captured at mount, so unassigned pieces that the 3D
      // preparation dropped (e.g. missing dimensions) stay visible too.
      visibleRfqIds = this.allUnplannedRfqIds || null
    } else if (filter && filter.type === 'keys') {
      const keys = filter.keys || []
      const items = this.itemsByKey ? Array.from(this.itemsByKey.values()) : []
      const scopedItems = items.filter((item) => item.source_type === 'unplanned_load' && keys.includes(item.key))
      visibleRfqIds = scopedItems.length ? new Set(scopedItems.map((item) => String(item.rfq_id))) : null
    }

    // An empty scope means the filter has nothing to say about the pieces table
    // (fleet-truck filters, or no mappable rfq ids) — never blank every row.
    const scoped = Boolean(visibleRfqIds && visibleRfqIds.size)
    rows.forEach((row) => {
      const hidden = scoped && !visibleRfqIds.has(String(row.dataset.rfqId))
      row.classList.toggle('is-filtered-out', hidden)
    })
    this.hideOrphanedRfqDetailRows()
  }

  // Bids/source detail rows hang off their parent load row; keep them hidden
  // whenever the parent is filtered out (they are display-toggled separately).
  hideOrphanedRfqDetailRows() {
    const hiddenParentIds = new Set()
    document.querySelectorAll('tr[data-rfq-row="true"].is-filtered-out').forEach((row) => {
      if (row.dataset.rfqId) hiddenParentIds.add(String(row.dataset.rfqId))
    })

    document.querySelectorAll('tr[data-rfq-parent-id], tr[data-rfq-source-parent-id]').forEach((detailRow) => {
      const parentId = detailRow.dataset.rfqParentId || detailRow.dataset.rfqSourceParentId
      detailRow.classList.toggle('is-filtered-out', hiddenParentIds.has(String(parentId)))
    })
  }

  loadClassByRfqId() {
    const map = new Map()
    document.querySelectorAll('tr[data-rfq-row="true"]').forEach((row) => {
      if (row.dataset.rfqId && row.dataset.loadClass) {
        map.set(String(row.dataset.rfqId), row.dataset.loadClass)
      }
    })
    return map
  }

  syncFilterBar(filter) {
    if (!this.hasFilterBarTarget) return

    // Equipment/truck filters are already represented by the passive in-canvas
    // state label and the single Back action. Keep the legacy bar only for the
    // separate Unassigned Pieces table, where the 3D viewer may be off-screen.
    const showLegacyBar = Boolean(filter && filter.type === 'unassigned')
    this.filterBarTarget.classList.toggle('hidden', !showLegacyBar)
    if (this.hasFilterLabelTarget) {
      this.filterLabelTarget.textContent = filter ? filter.label : ''
    }
  }

  syncSummaryActiveState(filter) {
    const signature = filter && (filter.signature || filter.type)
    document
      .querySelectorAll('[data-behavior~="project-summary-bucket"], [data-behavior~="project-summary-class"], [data-behavior~="project-summary-unassigned"]')
      .forEach((row) => {
        let rowSignature = null
        if (row.matches('[data-behavior~="project-summary-bucket"]')) {
          rowSignature = this.filterForBucketRow(row).signature
        } else if (row.matches('[data-behavior~="project-summary-class"]')) {
          rowSignature = `class:${row.dataset.loadClass || ''}`
        } else {
          rowSignature = 'unassigned'
        }
        row.classList.toggle('is-active', Boolean(signature) && rowSignature === signature)
      })
  }

  scrollUnplannedWarningIntoView() {
    const warning = document.querySelector('[data-load-planning-unplanned-warning="true"]')
    if (warning && typeof warning.scrollIntoView === 'function') {
      warning.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  highlightRow(key) {
    if (!this.hasRowGroupTarget) return

    this.rowGroupTargets.forEach((group) => {
      group.classList.toggle('is-hovered', Boolean(key) && group.dataset.key === key)
    })
  }

  clearHoveredRow() {
    this.highlightRow(null)
  }

  destroyRenderers() {
    if (this.fleetRenderer) {
      this.fleetRenderer.destroy()
      this.fleetRenderer = null
    }
  }

  showFleetPlaceholder(reason) {
    if (this.fleetRenderer) {
      this.fleetRenderer.destroy()
      this.fleetRenderer = null
    }

    const wrapper = document.createElement('div')
    wrapper.className = 'ttRenderFallback'

    const body = document.createElement('div')
    body.className = 'ttRenderFallbackBody'

    const title = document.createElement('strong')
    title.textContent = 'Fleet preview unavailable'

    const detail = document.createElement('div')
    detail.textContent = String(reason || 'fleet_render_unavailable')

    body.appendChild(title)
    body.appendChild(detail)
    wrapper.appendChild(body)

    this.fleetContainerTarget.replaceChildren(wrapper)
  }

  initialSelectedKey() {
    return this.element.dataset.selectedKey || null
  }

  consumePendingFleetViewRequest() {
    const pending = window.projectLoadPlanningPendingFleetView === true
    window.projectLoadPlanningPendingFleetView = null
    return pending
  }

  previewClearedByServer() {
    return this.element.dataset.previewCleared === 'true'
  }

  readInitialPreviewLoadRfqUnitId() {
    const value = this.element.dataset.selectedLoadRfqUnitId
    return value ? Number(value) : null
  }

  itemForGroupKey(groupKey) {
    return Array.from(this.itemsByKey.values()).find((item) => item.group_key === groupKey)
  }

  selectedDraftGroupKey(itemKey) {
    if (!itemKey || !String(itemKey).startsWith('draft:')) return null
    return String(itemKey).slice('draft:'.length)
  }

  csrfToken() {
    const element = document.querySelector('meta[name="csrf-token"]')
    return element ? element.content : ''
  }

  get renderPayload() {
    if (this._renderPayload !== undefined) return this._renderPayload

    try {
      this._renderPayload = JSON.parse(this.payloadTarget.textContent)
    } catch (_error) {
      this._renderPayload = null
    }

    return this._renderPayload
  }
}
